import React, { useState, useEffect } from 'react';
import '../styles/Leads.css';
import '../styles/RendezVous.css'; // Import pour le modal de rendez-vous
import '../styles/icons.css';
import '../styles/TableSorting.css';
import LeadProcessHelp from './LeadProcessHelp';
import CreateLeadForm from './CreateLeadForm';
import LeadsList from './LeadsList';
import ConvertLeadModal from './ConvertLeadModal';
import ImportExcelModal from './ImportExcelModal';
import ConsultationManager from './ConsultationManager';
import SourceSelector from './SourceSelector';
import GoogleSheetsManager from './GoogleSheetsManager';
import PortalDropdown from './PortalDropdown';
import { convertLeadToClient } from '../services/leadsAPI';
import { getConseillers, getConseillerNames } from '../services/conseillerAPI';
import { consultationService } from '../services/consultationAPI';
import { connectSocket } from '../services/socket';
import { useNotifications } from '../hooks/useNotifications';
import { useTaches } from '../hooks/useTaches';
import rendezVousAPI from '../services/rendezVousAPI';
import axios from 'axios';

// Animation CSS pour le modal
const modalAnimation = `
  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(-20px) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
`;

// Injecter l'animation dans le document
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement("style");
  styleSheet.innerText = modalAnimation;
  document.head.appendChild(styleSheet);
}

// Base API dynamique : utilise REACT_APP_API_URL depuis .env ou /api en production
// sinon fallback sur chemin relatif /api pour passer via le proxy Nginx.
const RAW_API_BASE = process.env.REACT_APP_API_URL || '/api';
const API_BASE = RAW_API_BASE.replace(/\/$/, '');
const CLOSURE_REASONS = [
  'Numéro invalide',
  'Ne répond pas après relances',
  'Doublon',
  'Pas intéressé',
  'Hors cible'
];

// Ajouter automatiquement le token d'authentification à toutes les requêtes Axios
const storedToken = localStorage.getItem('token');
if (storedToken) {
  axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
}

function Leads({ userRole = 'conseillere', navigationParams = {}, onNavigate = () => {} }) {
  // Supprimer le state showApiLeads - utiliser toujours l'interface originale avec API
  const notify = useNotifications(); // 🎯 Hook pour les notifications modernes
  const { ajouterTache } = useTaches(); // 🎯 Hook pour gérer les tâches
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [dropdownAnchorEl, setDropdownAnchorEl] = useState(null);
  
  // Récupérer le nom de l'utilisateur connecté depuis localStorage
  const userName = localStorage.getItem('userName') || '';

  // États pour la conversion de lead en client
  const [selectedLeadForConversion, setSelectedLeadForConversion] = useState(null);
  const [showConvertModal, setShowConvertModal] = useState(false);

  // États pour le tri
  const [sortField, setSortField] = useState('dateCreation');
  const [sortOrder, setSortOrder] = useState('desc');

  // États pour la gestion des sources de leads
  const [currentLeadSource, setCurrentLeadSource] = useState('database');
  const [showGoogleSheetsManager, setShowGoogleSheetsManager] = useState(false);

  // 🎯 États pour le modal de rendez-vous
  const [showRendezVousModal, setShowRendezVousModal] = useState(false);
  const [selectedLeadForRendezVous, setSelectedLeadForRendezVous] = useState(null);
  const [nouveauRdv, setNouveauRdv] = useState({
    client: '',
    client_id: null,
    client_type: 'lead',
    conseillere: '',
    date: '',
    heureDebut: '',
    heureFin: '',
    type: '',
    notes: ''
  });
  const [isLoadingConseillersRdv, setIsLoadingConseillersRdv] = useState(false);

  // Options statiques pour les rendez-vous
  const typeRendezVousOptions = ['Consultation initiale', 'Suivi de dossier', 'Consultation finale', 'Autre'];
  const creneauxHoraires = [
    '08:00', '08:30', '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
    '12:00', '12:30', '13:00', '13:30', '14:00', '14:30', '15:00', '15:30',
    '16:00', '16:30', '17:00', '17:30', '18:00', '18:30', '19:00'
  ];

  // Google Sheets maintenant disponible avec les vrais credentials
  const isGoogleSheetsAvailable = true; // Activé - credentials installés

  const handleLeadCreated = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  // 🎯 Fonctions pour gérer le modal de rendez-vous
  const handleNouveauRdvChange = (e) => {
    const { name, value } = e.target;
    setNouveauRdv(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const fermerRendezVousModal = () => {
    setShowRendezVousModal(false);
    setSelectedLeadForRendezVous(null);
    setNouveauRdv({
      client: '',
      client_id: null,
      client_type: 'lead',
      conseillere: '',
      date: '',
      heureDebut: '',
      heureFin: '',
      type: '',
      notes: ''
    });
  };

  const ajouterRendezVous = async (e) => {
    e.preventDefault();
    
    try {
      console.log('📅 Données du rendez-vous AVANT envoi:', nouveauRdv);
      console.log('👤 userRole:', userRole);
      console.log('👤 userName:', userName);
      
      // Validation : vérifier que conseillere est rempli
      if (!nouveauRdv.conseillere) {
        console.error('❌ Conseillère manquante !');
        notify.error('Erreur', 'Veuillez sélectionner une conseillère');
        return;
      }
      
      // ⚠️ IMPORTANT : Le backend attend des noms de champs en snake_case
      const dataToSend = {
        client_nom: nouveauRdv.client,              // client -> client_nom
        client_id: nouveauRdv.client_id,            // OK
        client_type: nouveauRdv.client_type,        // OK
        conseillere_nom: nouveauRdv.conseillere,    // conseillere -> conseillere_nom
        date_rdv: nouveauRdv.date,                  // date -> date_rdv
        heure_debut: nouveauRdv.heureDebut,         // heureDebut -> heure_debut
        heure_fin: nouveauRdv.heureFin,             // heureFin -> heure_fin
        type_rdv: nouveauRdv.type,                  // type -> type_rdv
        notes: nouveauRdv.notes,                     // OK
        statut: 'En attente'                         // OK
      };
      
      console.log('📤 Données envoyées à l\'API:', dataToSend);
      
      const result = await rendezVousAPI.createRendezVous(dataToSend);

      console.log('📥 Réponse de l\'API:', result);

      if (result.success) {
        notify.success('Rendez-vous créé', `Le rendez-vous avec ${nouveauRdv.client} a été planifié avec succès`);
        fermerRendezVousModal();
      } else {
        notify.error('Erreur', result.message || 'Impossible de créer le rendez-vous');
      }
    } catch (error) {
      console.error('❌ Erreur complète:', error);
      console.error('❌ Message d\'erreur:', error.message);
      console.error('❌ Réponse du serveur:', error.response?.data);
      notify.error('Erreur', error.response?.data?.message || error.message || 'Une erreur est survenue lors de la création du rendez-vous');
    }
  };

  // Charger les conseillères pour le modal de rendez-vous
  useEffect(() => {
    if (showRendezVousModal) {
      const loadConseilleresForRdv = async () => {
        try {
          setIsLoadingConseillersRdv(true);
          const conseillers = await getConseillers();
          // Utiliser conseilleresOptions qui existe déjà dans le composant
          if (Array.isArray(conseillers)) {
            // On ne fait rien car conseilleresOptions est déjà géré ailleurs
          }
        } catch (error) {
          console.error('Erreur lors du chargement des conseillères:', error);
        } finally {
          setIsLoadingConseillersRdv(false);
        }
      };
      loadConseilleresForRdv();
    }
  }, [showRendezVousModal]);

  // Écoute des événements temps réel pour mettre à jour la liste des leads
  useEffect(() => {
    const socket = connectSocket();

    socket.on('leadCreated', () => {
      console.log('[Leads] Lead créé - actualisation de la liste');
      setRefreshTrigger(prev => prev + 1);
    });

    socket.on('leadUpdated', () => {
      console.log('[Leads] Lead mis à jour - actualisation de la liste');
      setRefreshTrigger(prev => prev + 1);
    });

    socket.on('leadDeleted', () => {
      console.log('[Leads] Lead supprimé - actualisation de la liste');
      setRefreshTrigger(prev => prev + 1);
    });

    socket.on('clientCreated', () => {
      console.log('[Leads] Client créé (conversion) - actualisation de la liste');
      setRefreshTrigger(prev => prev + 1);
    });

    return () => {
      socket.off('leadCreated');
      socket.off('leadUpdated');
      socket.off('leadDeleted');
      socket.off('clientCreated');
    };
  }, []);

  // Fonction de tri
  const handleSort = (field) => {
    if (sortField === field) {
      // Si on clique sur la même colonne, inverser l'ordre
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Si on clique sur une nouvelle colonne, définir le tri croissant par défaut
      setSortField(field);
      setSortOrder('asc');
    }
  };

  // Fonction pour obtenir l'icône de tri
  const getSortIcon = (field) => {
    if (sortField !== field) return '↕️';
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  // Fonction pour trier les leads
  const getSortedLeads = () => {
    const filteredLeads = getFilteredLeads();
    const sortedLeads = [...filteredLeads].sort((a, b) => {
      let aValue;
      let bValue;

      switch (sortField) {
        case 'nom':
          aValue = `${a.prenom} ${a.nom}`.toLowerCase();
          bValue = `${b.prenom} ${b.nom}`.toLowerCase();
          break;
        case 'email':
          aValue = a.email.toLowerCase();
          bValue = b.email.toLowerCase();
          break;
        case 'telephone':
          aValue = a.telephone;
          bValue = b.telephone;
          break;
        case 'source':
          aValue = a.source.toLowerCase();
          bValue = b.source.toLowerCase();
          break;
        case 'statut':
          aValue = a.statut.toLowerCase();
          bValue = b.statut.toLowerCase();
          break;
        case 'dateCreation':
          aValue = new Date(a.dateCreation).getTime();
          bValue = new Date(b.dateCreation).getTime();
          break;
        case 'conseillere':
          aValue = (a.conseillere || '').toLowerCase();
          bValue = (b.conseillere || '').toLowerCase();
          break;
        case 'interet':
          aValue = a.interet.toLowerCase();
          bValue = b.interet.toLowerCase();
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return sortedLeads;
  };

  // Fonction pour ouvrir le modal de conversion
  const openConvertModal = (lead) => {
    // Adapter les données du lead pour correspondre à l'interface ConvertLeadModal
    const adaptedLead = {
      id: lead.id,
      nom: lead.nom,
      prenom: lead.prenom,
      email: lead.email,
      telephone: lead.telephone,
      source: lead.source,
      interet: lead.interet,
      conseillere: lead.conseillere,
      statut: lead.statut
    };
    setSelectedLeadForConversion(adaptedLead);
    setShowConvertModal(true);
    setOpenDropdown(null);
  };

  // Fonction pour fermer le modal de conversion
  const closeConvertModal = () => {
    setSelectedLeadForConversion(null);
    setShowConvertModal(false);
  };

  // Fonction pour gérer la conversion de lead en client
  const handleConvertLead = async (leadId, utilisateur, notes) => {
    try {
      const response = await convertLeadToClient(leadId, utilisateur, notes);
      
      if (response.success) {
        // Mettre à jour l'état local du lead
        const updatedLeads = leads.map(lead => {
          if (lead.id === leadId) {
            return { ...lead, statut: 'Client' };
          }
          return lead;
        });
        setLeads(updatedLeads);
        
        // 🎯 Notification moderne pour conversion de lead
        const clientName = `${response.data.client.prenom} ${response.data.client.nom}`.trim();
        const actions = [
          {
            label: 'Voir le client',
            onClick: () => {
              console.log(`Navigation vers client ${response.data.numeroDossier}`);
              // Ici vous pouvez naviguer vers la fiche client
            },
            variant: 'primary',
            icon: '👤'
          }
        ];
        
        notify.success(
          'Lead converti avec succès !',
          `Client "${clientName}" créé avec le numéro de dossier: ${response.data.numeroDossier}`,
          actions
        );
        
        // Recharger les leads pour avoir les données à jour
        loadLeadsFromAPI();
      }
    } catch (error) {
      console.error('Erreur lors de la conversion:', error);
      notify.error(
        'Échec de conversion',
        `Impossible de convertir le lead: ${error.message}`
      );
      throw error;
    }
  };

  // Fonction pour gérer l'ouverture/fermeture du dropdown avec debounce
  const toggleDropdown = (leadId, event) => {
    event.preventDefault();
    event.stopPropagation();
    
    setOpenDropdown(prevOpen => {
      const newValue = prevOpen === leadId ? null : leadId;
      // Store the anchor element when opening, clear when closing
      setDropdownAnchorEl(newValue === leadId ? (event.currentTarget || event.target) : null);
      
      // Si on ouvre un dropdown, ajuster sa position si nécessaire
      if (newValue === leadId) {
        setTimeout(() => {
          const dropdownElement = event.target.closest('.actions-dropdown')?.querySelector('.actions-dropdown-menu');
          if (dropdownElement) {
            adjustDropdownPosition(dropdownElement, event.target);
          }
        }, 10);
      }
      
      return newValue;
    });
  };

  // Fonction pour ajuster la position du dropdown si il est coupé
  const adjustDropdownPosition = (dropdownElement, buttonElement) => {
    if (!dropdownElement || !buttonElement) return;
    
    const dropdownRect = dropdownElement.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    
    // Réinitialiser les classes de position
    dropdownElement.classList.remove('dropdown-top', 'dropdown-left');
    
    // Vérifier si le dropdown est coupé en bas
    if (dropdownRect.bottom > viewportHeight - 10) {
      dropdownElement.classList.add('dropdown-top');
    }
    
    // Vérifier si le dropdown est coupé à droite
    if (dropdownRect.right > viewportWidth - 10) {
      dropdownElement.classList.add('dropdown-left');
    }
  };

  // Fermer le dropdown avec une approche plus robuste
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdown === null) return;
      // Considérer les clics dans le menu portal comme internes
      const clickedInPortal = event.target.closest('.portal-dropdown') || event.target.closest('.actions-dropdown-menu');
      const isDropdownButton = event.target.closest('.actions-dropdown-button');
      const clickedInInlineContainer = event.target.closest('.actions-dropdown');
      if (clickedInPortal || isDropdownButton || clickedInInlineContainer) {
        return; // ne pas fermer
      }
      setOpenDropdown(null);
      setDropdownAnchorEl(null);
    };

    document.addEventListener('click', handleClickOutside, true);
    document.addEventListener('touchstart', handleClickOutside, true);
    
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
      document.removeEventListener('touchstart', handleClickOutside, true);
    };
  }, [openDropdown]);

  // Fonction pour obtenir l'icône correspondant au statut
  const getStatusIcon = (statut) => {
    switch(statut) {
      case 'Nouveau':
        return 'fa-star';
      case 'Contacté':
        return 'fa-phone';
      case 'À recontacter':
        return 'fa-phone-slash';
      case 'Rendez-vous pris':
        return 'fa-calendar-check';
      case 'Consultation effectuée':
        return 'fa-clipboard-check';
      case 'Consultation manquée':
        return 'fa-calendar-times';
      case 'Qualifié':
        return 'fa-user-check';
      case 'Non qualifié':
        return 'fa-user-times';
      case 'Pas intéressé':
        return 'fa-times-circle';
      case 'Fermé':
        return 'fa-lock';
      case 'Client':
        return 'fa-user-tie';
      default:
        return 'fa-circle';
    }
  };

  // État pour les leads - commence vide, sera chargé depuis l'API
  const [leads, setLeads] = useState([]);
  const [isLoadingLeads, setIsLoadingLeads] = useState(true);
  const [selectedLeadIds, setSelectedLeadIds] = useState([]);
  const [showBulkReassignModal, setShowBulkReassignModal] = useState(false);
  const [bulkConseillerIds, setBulkConseillerIds] = useState([]);
  const [isBulkReassigning, setIsBulkReassigning] = useState(false);
  
  // États pour la pagination
  const [currentPage, setCurrentPage] = useState(1);
  const leadsPerPage = 10;
  
  // Fonction pour charger les leads depuis l'API selon la source sélectionnée
  const loadLeadsFromAPI = async () => {
    console.log('🔄 Chargement des leads depuis l\'API...');
    console.log('📊 Source sélectionnée:', currentLeadSource);
    
    try {
      setIsLoadingLeads(true);
      
      // Configurer le token dynamiquement pour chaque requête
      const token = localStorage.getItem('token');
      const headers = {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
        console.log('🔑 Token configuré pour la requête:', token.substring(0, 20) + '...');
      } else {
        console.log('⚠️ Aucun token trouvé dans localStorage');
      }
      
      // Choisir l'endpoint selon la source sélectionnée
  let endpoint = `${API_BASE}/leads`; // Par défaut: base de données
      
      if (currentLeadSource === 'hybrid') {
  endpoint = `${API_BASE}/leads-hybrid`;
      } else if (currentLeadSource === 'sheets') {
  endpoint = `${API_BASE}/google-sheets/leads`;
      }
      
      console.log('🌐 Endpoint utilisé:', endpoint);
  const response = await axios.get(endpoint, { headers });
      
      console.log('📡 Réponse API loadLeads:', response.data);
      
      if (response.data.success) {
        // Adapter le traitement selon la source
        let apiLeads = [];
        
        if (currentLeadSource === 'hybrid' || currentLeadSource === 'sheets') {
          // Format hybride ou Google Sheets
          const leadsData = response.data.data?.leads || response.data.data || [];
          apiLeads = leadsData.map(lead => ({
            id: lead.id,
            nom: lead.nom,
            prenom: lead.prenom,
            email: lead.email,
            telephone: lead.telephone,
            source: lead.source,
            reference_details: lead.reference_details || '',
            motif_fermeture: lead.motif_fermeture || '',
            statut: lead.statut,
            dateCreation: lead.date_creation ? lead.date_creation.split('T')[0] : new Date().toISOString().split('T')[0],
            conseillere: lead.conseillere || '',
            interet: lead.interet,
            notes: lead.notes || ''
          }));
        } else {
          // Format traditionnel base de données
          const leadsData = response.data.data.leads || [];
          apiLeads = leadsData.map(lead => ({
            id: lead.id,
            nom: lead.nom,
            prenom: lead.prenom,
            email: lead.email,
            telephone: lead.telephone,
            source: lead.source,
            reference_details: lead.reference_details || '',
            motif_fermeture: lead.motif_fermeture || '',
            statut: lead.statut,
            dateCreation: lead.date_creation ? lead.date_creation.split('T')[0] : new Date().toISOString().split('T')[0],
            conseillere: lead.conseillere || '',
            interet: lead.interet,
            notes: lead.notes || ''
          }));
        }
        
        console.log('✅ Leads transformés:', apiLeads);
        console.log(`📊 ${apiLeads.length} leads chargés depuis: ${currentLeadSource}`);
        setLeads(apiLeads);
        setCurrentPage(1); // Revenir à la première page après rechargement
      }
    } catch (error) {
      console.error('❌ Erreur lors du chargement des leads:', error);
      // En cas d'erreur, garder les leads existants ou un tableau vide
    } finally {
      setIsLoadingLeads(false);
    }
  };

  // Charger les leads et les conseillers au démarrage du composant
  useEffect(() => {
    window.dispatchEvent(new Event('lead-followup-changed'));
  }, [leads]);

  // Charger les leads et les conseillers au démarrage du composant
  useEffect(() => {
    loadLeadsFromAPI();
    loadConseillers();
  }, []);

  // Recharger les leads quand la source change
  useEffect(() => {
    console.log('🔄 Source changée vers:', currentLeadSource);
    loadLeadsFromAPI();
  }, [currentLeadSource]);

  // Fonction pour charger les conseillers et secrétaires depuis l'API
  const loadConseillers = async () => {
    try {
      console.log('🔄 Chargement des conseillers et secrétaires pour Leads.js...');
      const result = await getConseillers();
      
      if (result.success && result.data) {
        setConseillersList(result.data);
        const conseillerNames = getConseillerNames(result.data);
        
        // Pour les conseillères, s'assurer que leur nom est inclus
        if (userRole === 'conseillere') {
          const userName = localStorage.getItem('userName');
          if (userName && !conseillerNames.includes(userName)) {
            console.log('👤 Ajout du nom de la conseillère connectée:', userName);
            conseillerNames.push(userName);
          }
          setConseilleresOptions(conseillerNames);
        } else if (userRole === 'secretaire') {
          // Pour les secrétaires, s'assurer que leur nom est inclus
          const userName = localStorage.getItem('userName');
          if (userName && !conseillerNames.includes(userName)) {
            console.log('👤 Ajout du nom de la secrétaire connectée:', userName);
            conseillerNames.push(userName);
          }
          setConseilleresOptions(conseillerNames);
        } else {
          setConseilleresOptions(conseillerNames);
        }
        
        console.log('✅ Conseillers et secrétaires chargés dans Leads.js:', conseillerNames);
      } else {
        // En cas d'échec, au moins inclure la conseillère ou secrétaire connectée
        if (userRole === 'conseillere') {
          const userName = localStorage.getItem('userName');
          if (userName) {
            setConseilleresOptions([userName]);
            console.log('🔧 Fallback: ajout de la conseillère connectée:', userName);
          }
        } else if (userRole === 'secretaire') {
          const userName = localStorage.getItem('userName');
          if (userName) {
            setConseilleresOptions([userName]);
            console.log('🔧 Fallback: ajout de la secrétaire connectée:', userName);
          }
        } else {
          const defaultNames = getConseillerNames(result.data);
          setConseilleresOptions(defaultNames);
        }
      }
    } catch (error) {
      console.error('❌ Erreur lors du chargement des conseillers et secrétaires dans Leads.js:', error);
      
      // En cas d'erreur, au moins inclure la conseillère ou secrétaire connectée
      if (userRole === 'conseillere') {
        const userName = localStorage.getItem('userName');
        if (userName) {
          setConseilleresOptions([userName]);
          console.log('🔧 Erreur fallback: ajout de la conseillère connectée:', userName);
        }
      } else if (userRole === 'secretaire') {
        const userName = localStorage.getItem('userName');
        if (userName) {
          setConseilleresOptions([userName]);
          console.log('🔧 Erreur fallback: ajout de la secrétaire connectée:', userName);
        }
      }
    }
  };

  // État pour les filtres
  const [filtres, setFiltres] = useState({
    statut: '',
    source: '',
    conseillere: ''
  });
  
  // État pour la recherche
  const [searchTerm, setSearchTerm] = useState('');
  
  // État pour stocker le nom de la conseillère connectée
  const [nomConseillerConnectee, setNomConseillerConnectee] = useState('');
  
  // État pour les erreurs de validation
  const [emailError, setEmailError] = useState('');
  
  // État pour le nouveau lead
  const [nouveauLead, setNouveauLead] = useState({
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    source: 'Site web',
    referenceDetails: '',
    statut: 'Nouveau',
    conseillere: '',
    interet: '',
    notes: ''
  });
  
  // État pour le lead sélectionné (vue détaillée)
  const [selectedLead, setSelectedLead] = useState(null);
  
  // État pour les modales
  const [showModal, setShowModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false); // Modal de création de tâche
  const [showProcessHelp, setShowProcessHelp] = useState(false);
  const [showStatutChangeModal, setShowStatutChangeModal] = useState(false);
  const [showCloseLeadModal, setShowCloseLeadModal] = useState(false);
  const [leadToClose, setLeadToClose] = useState(null);
  const [closureReason, setClosureReason] = useState('');
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [leadToDelete, setLeadToDelete] = useState(null);
  const [statutChangeData, setStatutChangeData] = useState({
    oldStatut: '',
    newStatut: '',
    commentaire: ''
  });
  
  // État pour l'assignation
  const [assignData, setAssignData] = useState({
    leadId: null,
    conseillere: ''
  });
  
  // État pour la tâche à créer
  const [taskData, setTaskData] = useState({
    leadId: null,
    leadNom: '',
    titre: '',
    description: '',
    echeance: '',
    priorite: 'important',
    categorie: 'client'
  });

  // État pour les conseillers
  const [conseilleresOptions, setConseilleresOptions] = useState(['wafaa chaouby', 'hame amni', 'sanaa sami']);
  // État pour la liste complète des conseillers (objet avec id & nom)
  const [conseillersList, setConseillersList] = useState([]);

  // Options pour les filtres et le formulaire
  const statutOptions = ['Nouveau', 'Contacté', 'À recontacter', 'Injoignable à rappeler', 'Rendez-vous pris', 'Consultation effectuée', 'Consultation manquée', 'Qualifié', 'Non qualifié', 'Pas intéressé', 'Fermé', 'Client'];
  const sourceOptions = ['Manuel', 'Google Sheets', 'Site web', 'LinkedIn', 'Facebook', 'Instagram', 'TikTok', 'Référence', 'Autre'];
  const interetOptions = ['Permis d\'études', 'Permis de travail', 'Résidence permanente', 'Visa visiteur', 'Citoyenneté', 'Autre'];

  // Récupérer le nom de la conseillère ou secrétaire connectée au chargement
  useEffect(() => {
    if (userRole === 'conseillere' || userRole === 'secretaire') {
      const userName = localStorage.getItem('userName');
      if (userName) {
        setNomConseillerConnectee(userName);
        console.log(`👤 Nom de la ${userRole} connectée défini:`, userName);
      }
    }
  }, [userRole]);

  // 🎯 Appliquer les filtres de navigation (ex: depuis le tableau de bord)
  useEffect(() => {
    if (navigationParams && navigationParams.filtreStatut) {
      console.log('📊 Application du filtre de navigation:', navigationParams.filtreStatut);
      setFiltres(prevFiltres => ({
        ...prevFiltres,
        statut: navigationParams.filtreStatut
      }));
    }
    
    // 🎯 Ouvrir automatiquement le détail d'un lead si demandé
    if (navigationParams && navigationParams.ouvrirDetailLead) {
      console.log('📂 Ouverture automatique du détail du lead:', navigationParams.ouvrirDetailLead);
      // Attendre que les leads soient chargés puis ouvrir le détail
      setTimeout(() => {
        const leadToOpen = leads.find(l => l.id === navigationParams.ouvrirDetailLead);
        if (leadToOpen) {
          openLeadDetail(leadToOpen);
        }
      }, 500);
    }
  }, [navigationParams, leads]);

  // 🔄 Rafraîchir la liste après création d'un RDV depuis un lead (et nettoyer le flag)
  useEffect(() => {
    if (navigationParams && navigationParams.refreshAfterRdv) {
      console.log('🔄 Rafraîchissement des leads suite à un rendez-vous');
      loadLeadsFromAPI();
      // Nettoyer les paramètres pour éviter des boucles
      try { onNavigate && onNavigate('leads', {}); } catch (_) {}
    }
  }, [navigationParams]);

  // 🎯 Ouvrir automatiquement un lead depuis le localStorage (ex: depuis le tableau de bord)
  useEffect(() => {
    const selectedLeadId = localStorage.getItem('selectedLeadId');
    
    if (selectedLeadId && leads.length > 0) {
      console.log('📂 [Leads] Ouverture automatique du lead depuis localStorage:', selectedLeadId);
      
      // Chercher le lead dans la liste
      const leadToOpen = leads.find(l => l.id === parseInt(selectedLeadId));
      
      if (leadToOpen) {
        console.log('✅ [Leads] Lead trouvé, ouverture du détail:', leadToOpen);
        openLeadDetail(leadToOpen);
        // Nettoyer le localStorage après utilisation
        localStorage.removeItem('selectedLeadId');
      } else {
        console.log('❌ [Leads] Lead non trouvé avec ID:', selectedLeadId);
        // Nettoyer quand même le localStorage
        localStorage.removeItem('selectedLeadId');
      }
    }
  }, [leads]); // Se déclenche quand la liste des leads change

  // Préremplir automatiquement le champ conseillère avec l'utilisateur connecté (seulement pour les conseillères)
  useEffect(() => {
    // Pour les conseillères seulement, préremplir automatiquement avec leur nom
    // Les secrétaires et administrateurs laissent vide pour l'assignation automatique équitable
    if (userRole === 'conseillere' && !nouveauLead.conseillere) {
      const userName = localStorage.getItem('userName');
      console.log(`👤 Utilisateur connecté récupéré (Leads.js) - ${userRole}:`, userName);
      
      if (userName) {
        console.log(`✅ Préremplissage du champ conseillère avec conseillère:`, userName);
        setNouveauLead(prev => ({
          ...prev,
          conseillere: userName
        }));
      } else {
        console.log('⚠️ Aucun utilisateur connecté trouvé dans localStorage (Leads.js)');
      }
    }
    // Pour les autres rôles, le champ reste vide et l'assignation sera automatique et équitable
  }, [userRole, nouveauLead.conseillere]);
  
  // Filtrer les leads selon le rôle de l'utilisateur
  const getFilteredLeads = () => {
    // Filtrer d'abord par rôle
    let roleFiltered = [];
    
    if (userRole === 'administrateur' || userRole === 'directeur') {
      // Accès à tous les leads pour administrateur et directeur
      roleFiltered = [...leads];
    } else if (userRole === 'conseillere') {
      // Accès uniquement à ses propres leads  
      const userName = localStorage.getItem('userName');
      if (userName) {
        roleFiltered = leads.filter(lead => {
          if (!lead.conseillere) return true; // Leads non assignés
          // Vérifier si le lead appartient à la conseillère connectée
          return lead.conseillere.toLowerCase().includes(userName.toLowerCase()) ||
                 userName.toLowerCase().includes(lead.conseillere.toLowerCase());
        });
      } else {
        roleFiltered = leads.filter(lead => !lead.conseillere); // Seulement les non assignés si pas de nom
      }
    } else if (userRole === 'secretaire') {
      // Accès à tous les leads pour la secrétaire (nouvelle règle)
      roleFiltered = [...leads];
    } else {
      roleFiltered = [...leads];
    }
    
    // Ensuite, appliquer les filtres de recherche et de statut/source
    return roleFiltered.filter(lead => {
      // Appliquer la recherche globale améliorée (disponible pour tous les rôles)
      const searchMatch = searchTerm === '' || 
        `${lead.prenom} ${lead.nom}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
        lead.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        lead.telephone.includes(searchTerm) ||
        (lead.source && lead.source.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (lead.statut && lead.statut.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (lead.interet && lead.interet.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (lead.conseillere && lead.conseillere.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (lead.notes && lead.notes.toLowerCase().includes(searchTerm.toLowerCase()));
      
      // Appliquer les filtres
      const statutMatch = filtres.statut === '' || lead.statut === filtres.statut;
      const sourceMatch = filtres.source === '' || lead.source === filtres.source;
      const conseilleresMatch = filtres.conseillere === '' || lead.conseillere === filtres.conseillere;
      
      return searchMatch && statutMatch && sourceMatch && conseilleresMatch;
    });
  };
  
  const leadsFiltrés = getSortedLeads();
  const canBulkReassign = ['super_admin', 'admin', 'administrateur', 'directeur', 'manager', 'secretaire'].includes(userRole);
  const assignableDestinataires = conseillersList.filter((conseiller) => (
    ['conseillere', 'secretaire'].includes(conseiller.role) && conseiller.recoitLeads !== false
  ));
  const selectableLeads = leadsFiltrés.filter((lead) => lead.statut !== 'Fermé');
  const selectedLeadCount = selectedLeadIds.filter((id) => leads.some((lead) => lead.id === id && lead.statut !== 'Fermé')).length;
  const allFilteredLeadsSelected = selectableLeads.length > 0 && selectableLeads.every((lead) => selectedLeadIds.includes(lead.id));

  const toggleLeadSelection = (leadId) => {
    if (leads.find((lead) => lead.id === leadId)?.statut === 'Fermé') return;
    setSelectedLeadIds((previous) => (
      previous.includes(leadId) ? previous.filter((id) => id !== leadId) : [...previous, leadId]
    ));
  };

  const toggleAllFilteredLeads = () => {
    const filteredIds = selectableLeads.map((lead) => lead.id);
    setSelectedLeadIds((previous) => (
      allFilteredLeadsSelected
        ? previous.filter((id) => !filteredIds.includes(id))
        : [...new Set([...previous, ...filteredIds])]
    ));
  };

  const openBulkReassignModal = () => {
    if (selectedLeadCount === 0) {
      notify.warning('Aucun lead sélectionné', 'Cochez les leads à transférer, ou utilisez la case « Tous » après avoir filtré par conseillère ou secrétaire.');
      return;
    }
    setBulkConseillerIds([]);
    setShowBulkReassignModal(true);
  };

  const toggleBulkConseiller = (conseillerId) => {
    setBulkConseillerIds((previous) => (
      previous.includes(conseillerId) ? previous.filter((id) => id !== conseillerId) : [...previous, conseillerId]
    ));
  };

  const handleBulkReassign = async () => {
    if (bulkConseillerIds.length === 0) {
      notify.error('Destinataire requis', 'Sélectionnez au moins une conseillère active.');
      return;
    }
    try {
      setIsBulkReassigning(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE}/leads/bulk-reassign`,
        { lead_ids: selectedLeadIds, conseiller_ids: bulkConseillerIds },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      if (!response.data?.success) throw new Error(response.data?.message || 'Le transfert des leads a échoué');

      const details = response.data.data?.repartition
        ?.filter((item) => item.nombre_leads > 0)
        .map((item) => `${item.conseillere} : ${item.nombre_leads}`)
        .join(' · ');
      notify.success('Leads transférés', `${response.data.message}${details ? ` (${details})` : ''}`);
      setShowBulkReassignModal(false);
      setBulkConseillerIds([]);
      setSelectedLeadIds([]);
      await loadLeadsFromAPI();
    } catch (error) {
      console.error('Erreur transfert groupé:', error);
      notify.error('Transfert impossible', error.response?.data?.message || error.message || 'Une erreur est survenue lors du transfert des leads.');
    } finally {
      setIsBulkReassigning(false);
    }
  };
  
  // Fonctions pour la pagination
  const getTotalPages = () => {
    const totalPages = Math.ceil(leadsFiltrés.length / leadsPerPage);
    console.log(`📊 Pagination Debug - Total leads filtrés: ${leadsFiltrés.length}, Leads per page: ${leadsPerPage}, Total pages: ${totalPages}`);
    return totalPages;
  };

  const getPaginatedLeads = () => {
    const startIndex = (currentPage - 1) * leadsPerPage;
    const endIndex = startIndex + leadsPerPage;
    const paginated = leadsFiltrés.slice(startIndex, endIndex);
    console.log(`📄 Page ${currentPage}: Affichage ${startIndex + 1} à ${Math.min(endIndex, leadsFiltrés.length)} sur ${leadsFiltrés.length} leads`);
    return paginated;
  };

  const goToPage = (page) => {
    setCurrentPage(page);
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      goToPage(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < getTotalPages()) {
      goToPage(currentPage + 1);
    }
  };

  // Obtenir les leads paginés
  const leadsPaginés = getPaginatedLeads();
  const totalPages = getTotalPages();
  
  // Debug de la pagination
  console.log(`🔍 Pagination Status - Current page: ${currentPage}, Total pages: ${totalPages}, Show pagination: ${totalPages > 1}`);
  
  // Gérer les changements de filtres
  const handleFiltreChange = (e) => {
    const { name, value } = e.target;
    setFiltres({
      ...filtres,
      [name]: value
    });
    setCurrentPage(1); // Revenir à la première page lors du changement de filtre
  };
  
  // Réinitialiser les filtres
  const resetFiltres = () => {
    setFiltres({
      statut: '',
      source: '',
      conseillere: ''
    });
    setSearchTerm('');
    setCurrentPage(1); // Revenir à la première page
  };
  
  // Gérer les changements dans le formulaire de nouveau lead
  const handleNouveauLeadChange = (e) => {
    const { name, value } = e.target;
    
    // Réinitialiser l'erreur d'email quand l'utilisateur modifie le champ email
    if (name === 'email' && emailError) {
      setEmailError('');
    }
    
    setNouveauLead({
      ...nouveauLead,
      [name]: value
    });
  };

  // Fonction pour réinitialiser intelligemment le formulaire (préserve la conseillère pour les conseillères)
  const resetFormulaireLead = () => {
    // Réinitialiser l'erreur d'email
    setEmailError('');
    
    let conseillerePreremplie = '';
    
    // Pour les conseillères seulement, préremplir automatiquement avec leur nom
    // Les autres rôles (secrétaires, admins) laissent vide pour l'assignation automatique équitable
    if (userRole === 'conseillere') {
      const userName = localStorage.getItem('userName');
      if (userName) {
        conseillerePreremplie = userName;
      }
    }
    
    setNouveauLead({
      nom: '',
      prenom: '',
      email: '',
      telephone: '',
      source: 'Site web',
      referenceDetails: '',
      statut: 'Nouveau',
      conseillere: conseillerePreremplie,
      interet: '',
      notes: ''
    });
    
    console.log('🔄 Formulaire lead réinitialisé avec conseillère préremplie:', conseillerePreremplie);
  };

  // Fonction pour ouvrir la modal avec préremplissage automatique
  const ouvrirModalLead = () => {
    setShowModal(true);
    // Pour les conseillères, s'assurer que le champ est pré-rempli
    if (userRole === 'conseillere' && !nouveauLead.conseillere) {
      const userName = localStorage.getItem('userName');
      if (userName) {
        console.log('✅ Préremplissage modal conseillère:', userName);
        setNouveauLead(prev => ({
          ...prev,
          conseillere: userName
        }));
      }
    }
  };

  // Fonction pour fermer la modal avec réinitialisation intelligente
  const fermerModalLead = () => {
    setShowModal(false);
    resetFormulaireLead();
  };

  // Ajouter un nouveau lead via API
  const ajouterLead = async (e) => {
    e.preventDefault();
    
    // Réinitialiser l'erreur d'email
    setEmailError('');
    
    console.log('🔍 Debug - nouveauLead:', {
      source: nouveauLead.source,
      referenceDetails: nouveauLead.referenceDetails,
      hasReferenceDetails: !!nouveauLead.referenceDetails,
      referenceDetailsLength: nouveauLead.referenceDetails ? nouveauLead.referenceDetails.length : 'undefined'
    });
    
    console.log('🔍 ÉTAT COMPLET nouveauLead:', JSON.stringify(nouveauLead, null, 2));
    
    // Validation conditionnelle pour les détails de la référence
    if (nouveauLead.source === 'Référence' && (!nouveauLead.referenceDetails || !nouveauLead.referenceDetails.trim())) {
      notify.validationError('Détails de la référence requis');
      return;
    }
    
    try {
      // Préparer les données à envoyer
      const dataToSend = {
        nom: nouveauLead.nom,
        prenom: nouveauLead.prenom,
        email: nouveauLead.email,
        telephone: nouveauLead.telephone,
        source: nouveauLead.source,
        reference_details: nouveauLead.referenceDetails, // Mapper vers le nom attendu par le backend
        interet: nouveauLead.interet,
        conseillere: nouveauLead.conseillere,
        notes: nouveauLead.notes
      };
      
      console.log('📤 Données envoyées au backend:', dataToSend);
      
      // Récupérer le token d'authentification
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json'
      };
      
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
      
      console.log('🔑 Headers envoyés:', headers);
      
      // Envoyer le lead à l'API backend
  const response = await axios.post(`${API_BASE}/leads`, dataToSend, {
        headers: headers
      });

      if (response.data.success) {
        // Ajouter le nouveau lead à l'état local au lieu de recharger tout
        const newLead = {
          id: response.data.data.id,
          nom: nouveauLead.nom,
          prenom: nouveauLead.prenom,
          email: nouveauLead.email,
          telephone: nouveauLead.telephone,
          source: nouveauLead.source,
          referenceDetails: nouveauLead.referenceDetails,
          statut: 'Nouveau',
          dateCreation: new Date().toISOString().split('T')[0],
          conseillere: nouveauLead.conseillere,
          interet: nouveauLead.interet,
          notes: nouveauLead.notes
        };
        
        setLeads(prevLeads => [...prevLeads, newLead]);
        
        // Réinitialiser le formulaire avec préremplissage de la conseillère
        resetFormulaireLead();
        
        setShowModal(false);
        
        // 🎯 Notification moderne avec actions
        notify.leadCreated(
          newLead.id,
          `${nouveauLead.nom} ${nouveauLead.prenom}`.trim() || 'Lead',
          (leadId) => {
            // Action: Voir la fiche du lead - Naviguer vers Leads avec ouverture auto du détail
            onNavigate('leads', { ouvrirDetailLead: leadId });
          },
          (leadId) => {
            // Action: Créer une tâche - Naviguer vers le module Mes Tâches avec ouverture auto du modal
            onNavigate('mes-taches', { ouvrirCreation: true });
          }
        );
      }
    } catch (error) {
      console.error('Erreur lors de la création du lead:', error);
      
      // Vérifier si c'est une erreur d'email existant
      const errorMessage = error.response?.data?.message || error.message || '';
      if (errorMessage.includes('email existe déjà')) {
        setEmailError('Un lead avec cet email existe déjà');
      } else {
        notify.error(
          'Échec de création du lead',
          errorMessage || 'Une erreur inattendue est survenue'
        );
      }
    }
  };
  
  // Changer le statut d'un lead et sauvegarder en base de données
  const changerStatut = async (id, nouveauStatut) => {
    console.log(`🔄 Tentative de changement de statut pour lead ${id}: ${nouveauStatut}`);
    
    // Vérifier si le lead est déjà converti en client
    const lead = leads.find(l => l.id === id);
    if (lead && lead.statut === 'Client') {
      notify.warning(
        'Modification impossible',
        'Ce lead a été converti en client et son statut est verrouillé.'
      );
      return;
    }
    
    try {
      // Mettre à jour le statut via l'API
  const response = await axios.put(`${API_BASE}/leads/${id}`, {
        statut: nouveauStatut
      });

      console.log('📡 Réponse API:', response.data);

      if (response.data.success) {
        // Mettre à jour l'état local après succès de l'API
        const updatedLeads = leads.map(lead => {
          if (lead.id === id) {
            return { ...lead, statut: nouveauStatut, motif_fermeture: nouveauStatut === 'Fermé' ? lead.motif_fermeture : '' };
          }
          return lead;
        });
        setLeads(updatedLeads);
        
        console.log(`✅ Statut du lead ${id} mis à jour: ${nouveauStatut}`);
      }
    } catch (error) {
      console.error('❌ Erreur lors de la mise à jour du statut:', error);
      console.error('📊 Détails de l\'erreur:', error.response?.data);
      notify.error(
        'Erreur de mise à jour',
        `Impossible de mettre à jour le statut: ${error.response?.data?.message || error.message}`
      );
    }
  };

  const openCloseLeadModal = (lead) => {
    setLeadToClose(lead);
    setClosureReason('');
    setShowCloseLeadModal(true);
    setOpenDropdown(null);
    setDropdownAnchorEl(null);
  };

  const closeLead = async () => {
    if (!leadToClose || !CLOSURE_REASONS.includes(closureReason)) {
      notify.warning('Motif obligatoire', 'Choisissez le motif de fermeture du lead.');
      return;
    }

    try {
      const response = await axios.put(`${API_BASE}/leads/${leadToClose.id}`, {
        statut: 'Fermé',
        motif_fermeture: closureReason
      });
      if (!response.data?.success) throw new Error(response.data?.message || 'Impossible de fermer ce lead');

      setLeads((previous) => previous.map((lead) => (
        lead.id === leadToClose.id
          ? { ...lead, statut: 'Fermé', motif_fermeture: closureReason }
          : lead
      )));
      setSelectedLeadIds((previous) => previous.filter((id) => id !== leadToClose.id));
      notify.success('Lead fermé', `Le lead est fermé : ${closureReason}. Il ne sera plus relancé ni réassigné.`);
      setShowCloseLeadModal(false);
      setLeadToClose(null);
      setClosureReason('');
    } catch (error) {
      console.error('Erreur fermeture lead:', error);
      notify.error('Fermeture impossible', error.response?.data?.message || error.message || 'Une erreur est survenue.');
    }
  };
  
  // Ouvrir la vue détaillée d'un lead
  const openLeadDetail = (lead) => {
    // Pour les conseillères seulement, pré-remplir avec leur nom si le lead n'est pas assigné
    // Les secrétaires peuvent voir et assigner à n'importe qui
    if (userRole === 'conseillere') {
      const userName = localStorage.getItem('userName');
      console.log(`👤 Conseillère connectée:`, userName);
      
      // Si le lead n'a pas de conseillère assignée, assigner automatiquement la conseillère connectée
      if ((!lead.conseillere || lead.conseillere === '' || lead.conseillere === 'À assigner') && userName) {
        console.log(`🔄 Attribution automatique de la conseillère connectée`);
        
        // Utiliser directement le nom de l'utilisateur connecté
        setSelectedLead({
          ...lead,
          conseillere: userName,
          _originalStatut: lead.statut // 🔒 Sauvegarder le statut original
        });
        console.log(`✅ Conseillère assignée automatiquement:`, userName);
      } else {
        // Garder l'assignation existante
        setSelectedLead({
          ...lead,
          _originalStatut: lead.statut // 🔒 Sauvegarder le statut original
        });
      }
    } else {
      // Pour les secrétaires et autres rôles, garder le lead tel quel (pas d'auto-assignation)
      setSelectedLead({
        ...lead,
        _originalStatut: lead.statut // 🔒 Sauvegarder le statut original
      });
    }
    setShowDetailModal(true);
  };
  
  // Mettre à jour un lead via l'API
  const updateLead = async (e) => {
    e.preventDefault();
    
    console.log(`🔄 Mise à jour du lead ${selectedLead.id} via l'API...`);
    
    try {
      // Auto-assignation SEULEMENT pour les conseillères, PAS pour les secrétaires
      // Secrétaires et administrateurs ont le MÊME comportement = contrôle total
      let leadDataToUpdate = { ...selectedLead };
      
      // AUTO-ASSIGNATION seulement pour les conseillères sur leads non assignés
      if (userRole === 'conseillere' && leadDataToUpdate.statut !== 'Fermé' && (!selectedLead.conseillere || selectedLead.conseillere === '' || selectedLead.conseillere === 'À assigner')) {
        const userName = localStorage.getItem('userName');
        if (userName) {
          console.log(`✅ Attribution automatique à la conseillère:`, userName);
          leadDataToUpdate.conseillere = userName;
          setSelectedLead(leadDataToUpdate);
        }
      }
      
      // SECRÉTAIRES et ADMINISTRATEURS : même comportement = respect total du choix utilisateur
      if (userRole === 'secretaire' || userRole === 'administrateur' || userRole === 'directeur') {
        console.log(`🔄 ${userRole} - respect total du choix d'assignation:`, selectedLead.conseillere);
      }
      
      console.log(`🔄 Sauvegarde lead ${selectedLead.id} - conseillère finale:`, leadDataToUpdate.conseillere);
      
      // Vérifier si le statut change vers "Consultation effectuée"
      const previousLead = leads.find(lead => lead.id === selectedLead.id);
      const isConsultationEffectuee = leadDataToUpdate.statut === 'Consultation effectuée' && 
                                     previousLead?.statut !== 'Consultation effectuée';
      
      // Détecter les changements d'assignation
      const assignationChanged = leadDataToUpdate.statut !== 'Fermé' && previousLead?.conseillere !== leadDataToUpdate.conseillere;
      const isReassignation = previousLead?.conseillere && previousLead.conseillere !== leadDataToUpdate.conseillere;
      
      // Préparer les données pour l'API
      const updateData = {
        nom: leadDataToUpdate.nom,
        prenom: leadDataToUpdate.prenom,
        email: leadDataToUpdate.email,
        telephone: leadDataToUpdate.telephone,
        source: leadDataToUpdate.source,
        reference_details: leadDataToUpdate.reference_details, // Ajouter le champ reference_details
        statut: leadDataToUpdate.statut,
        interet: leadDataToUpdate.interet,
        notes: leadDataToUpdate.notes
      };

      // Le motif ne s'applique qu'à un lead fermé. Ne pas envoyer une chaîne
      // vide lors des modifications ordinaires, sinon la validation serveur
      // considérerait à tort le motif comme invalide.
      if (leadDataToUpdate.statut === 'Fermé') {
        updateData.motif_fermeture = leadDataToUpdate.motif_fermeture;
      }

      // Une fermeture ne doit jamais entraîner d'assignation ou de
      // réassignation implicite. L'assignation historique est conservée.
      if (leadDataToUpdate.statut !== 'Fermé') {
        updateData.conseillere = leadDataToUpdate.conseillere;
      }

      // Envoyer la mise à jour à l'API
  const response = await axios.put(`${API_BASE}/leads/${selectedLead.id}`, updateData);
      
      console.log('📡 Réponse API updateLead:', response.data);

      if (response.data.success) {
        // Si consultation effectuée, créer une consultation
        if (isConsultationEffectuee) {
          try {
            // Trouver l'ID du conseiller
            console.log('🔍 Recherche du conseiller:', leadDataToUpdate.conseillere);
            console.log('🔍 Liste des conseillers:', conseillersList);
            
            // Chercher le conseiller par différentes méthodes
            let conseiller = null;
            
            // 1. Recherche exacte par nomComplet
            conseiller = conseillersList.find(c => c.nomComplet === leadDataToUpdate.conseillere);
            
            // 2. Recherche par userName SEULEMENT si pas d'assignation explicite et conseillère connectée
            if (!conseiller && userRole === 'conseillere' && 
                (!leadDataToUpdate.conseillere || leadDataToUpdate.conseillere === 'À assigner')) {
              const userName = localStorage.getItem('userName');
              const userId = localStorage.getItem('userId');
              console.log('🔍 Auto-assignation consultation - userName:', userName, 'userId:', userId);
              
              if (userId) {
                conseiller = conseillersList.find(c => c.id === parseInt(userId));
                console.log('🔍 Conseiller trouvé par userId pour auto-assignation:', conseiller);
              } else if (userName) {
                // Recherche par nom partiel
                conseiller = conseillersList.find(c => 
                  c.nomComplet && (
                    c.nomComplet.toLowerCase().includes(userName.toLowerCase()) ||
                    userName.toLowerCase().includes(c.nomComplet.toLowerCase())
                  )
                );
                console.log('🔍 Conseiller trouvé par recherche partielle pour auto-assignation:', conseiller);
              }
            }
            
            // 3. Recherche par nom/prénom séparés
            if (!conseiller) {
              const [prenom, ...nomParts] = leadDataToUpdate.conseillere.split(' ');
              const nom = nomParts.join(' ');
              conseiller = conseillersList.find(c => 
                c.prenom === prenom && c.nom === nom
              );
              console.log('🔍 Conseiller trouvé par nom/prénom:', conseiller);
            }
            
            console.log('🔍 Conseiller final trouvé:', conseiller);
            
            if (conseiller) {
              console.log('📝 Création de la consultation pour le conseiller ID:', conseiller.id);
              await consultationService.createConsultation(
                leadDataToUpdate.id || selectedLead.id,
                conseiller.id,
                'Consultation effectuée - mise à jour du statut'
              );
              console.log('✅ Consultation créée automatiquement');
            } else {
              console.warn('⚠️ Impossible de trouver le conseiller pour créer la consultation');
              console.warn('⚠️ Lead.conseillere:', leadDataToUpdate.conseillere);
              console.warn('⚠️ userName:', localStorage.getItem('userName'));
              console.warn('⚠️ userId:', localStorage.getItem('userId'));
              console.warn('⚠️ Conseillers disponibles:', conseillersList.map(c => ({ id: c.id, nomComplet: c.nomComplet, prenom: c.prenom, nom: c.nom })));
            }
          } catch (consultationError) {
            console.error('❌ Erreur lors de la création de la consultation:', consultationError);
            // Ne pas bloquer la mise à jour du lead pour cette erreur
          }
        }
        
        // Mettre à jour l'état local après succès de l'API
        const updatedLeads = leads.map(lead => {
          if (lead.id === selectedLead.id) {
            return leadDataToUpdate; // Utiliser les données mises à jour
          }
          return lead;
        });
        setLeads(updatedLeads);
        setShowDetailModal(false);
        
        console.log(`✅ Lead ${selectedLead.id} mis à jour avec succès`);

        const convertedClient = response.data?.data?.client;
        if (convertedClient) {
          const dossier = response.data.data.numeroDossier;
          notify.success(
            'Lead converti en client',
            `Le client ${convertedClient.prenom || ''} ${convertedClient.nom || ''}`.trim()
              + (dossier ? ` a été créé dans le dossier ${dossier}.` : ' a été créé.')
          );
          return;
        }
        
        // Message personnalisé en fonction des changements
        let successMessage = 'Lead mis à jour avec succès !';
        let detailMessage = '';
        if (isConsultationEffectuee) {
          detailMessage += 'Consultation enregistrée automatiquement. ';
        }
        if (assignationChanged && isReassignation) {
          detailMessage += 'La nouvelle conseillère a été notifiée de cette réassignation.';
        } else if (assignationChanged) {
          detailMessage += 'La conseillère a été notifiée de cette assignation.';
        }
        
        notify.success(successMessage, detailMessage.trim());
      }
    } catch (error) {
      console.error('❌ Erreur lors de la mise à jour du lead:', error);
      console.error('📊 Détails de l\'erreur:', error.response?.data);
      
      // Gestion spécifique pour le statut verrouillé
      if (error.response?.data?.error === 'STATUT_VERROUILLE') {
        notify.warning(
          'Statut verrouillé',
          error.response.data.details || 'Une consultation effectuée ne peut être modifiée sans commentaire explicatif'
        );
        return;
      }
      
      notify.error(
        'Erreur de mise à jour',
        `Impossible de mettre à jour le lead: ${error.response?.data?.message || error.message}`
      );
    }
  };
  
  // Gérer les changements dans le formulaire de détail
  const handleDetailChange = (e) => {
    const { name, value } = e.target;
    
    // 🔒 Empêcher la modification du statut pour les leads ORIGINALEMENT convertis en clients
    // On vérifie le statut ORIGINAL, pas le statut actuel dans le state
    if (name === 'statut' && selectedLead._originalStatut === 'Client') {
      notify.warning(
        'Modification impossible',
        'Ce lead a été converti en client et son statut est verrouillé.'
      );
      return;
    }
    
    // 🔒 VALIDATION: Vérifier si on tente de changer le statut "Consultation effectuée"
    if (name === 'statut' && 
        selectedLead.statut === 'Consultation effectuée' && 
        value !== 'Consultation effectuée' && 
        userRole === 'conseillere') {
      
      // Ouvrir le modal de confirmation avec les informations
      setStatutChangeData({
        oldStatut: 'Consultation effectuée',
        newStatut: value,
        commentaire: ''
      });
      setShowStatutChangeModal(true);
      return;
    }
    
    // Liste des champs de base toujours modifiables
    const champsDeBase = ['nom', 'prenom', 'email', 'telephone', 'source', 'interet', 'ville', 'pays', 'age', 'profession', 'statut', 'notes', 'reference_details', 'motif_fermeture'];
    
    // Vérifier si l'utilisateur a le droit de modifier ce champ
    if (canEditAllFields() || champsDeBase.includes(name) || (name === 'conseillere' && canAssignLead())) {
      console.log(`✅ Modification autorisée du champ "${name}" avec la valeur "${value}" pour le rôle ${userRole}`);
      setSelectedLead({
        ...selectedLead,
        [name]: value
      });
    } else {
      console.log(`❌ Modification refusée du champ "${name}" pour le rôle ${userRole}`);
    }
  };
  
  // Confirmer le changement de statut avec commentaire
  const handleConfirmStatutChange = () => {
    if (!statutChangeData.commentaire || statutChangeData.commentaire.trim() === '') {
      notify.warning(
        'Commentaire obligatoire',
        'Un commentaire est obligatoire pour modifier le statut d\'une consultation effectuée.'
      );
      return;
    }
    
    // Ajouter le commentaire aux notes
    const now = new Date().toLocaleDateString('fr-FR') + ' à ' + new Date().toLocaleTimeString('fr-FR');
    const noteChangement = `[${now}] Changement de statut justifié: ${statutChangeData.commentaire}`;
    const nouvellesNotes = selectedLead.notes ? `${selectedLead.notes}\n\n${noteChangement}` : noteChangement;
    
    setSelectedLead({
      ...selectedLead,
      statut: statutChangeData.newStatut,
      notes: nouvellesNotes
    });
    
    notify.info(
      'Commentaire ajouté',
      'Votre justification a été ajoutée aux notes du lead.'
    );
    
    // Fermer le modal et réinitialiser
    setShowStatutChangeModal(false);
    setStatutChangeData({
      oldStatut: '',
      newStatut: '',
      commentaire: ''
    });
  };
  
  // Supprimer un lead (admin et directeur uniquement)
  const handleDeleteClick = (id) => {
    if (userRole === 'administrateur' || userRole === 'directeur' || userRole === 'admin') {
      // Trouver le lead à supprimer pour afficher son nom
      const lead = leads.find(lead => lead.id === id);
      setLeadToDelete(lead);
      setShowDeleteConfirmModal(true);
    }
  };

  const confirmDelete = async () => {
    if (!leadToDelete) return;

        try {
      const response = await axios.delete(`${API_BASE}/leads/${leadToDelete.id}`);
          
          if (response.data.success) {
            // Mettre à jour l'état local après suppression réussie
        const updatedLeads = leads.filter(lead => lead.id !== leadToDelete.id);
            setLeads(updatedLeads);
        console.log(`✅ Lead ${leadToDelete.id} supprimé avec succès`);
        
        // Fermer la modal
        setShowDeleteConfirmModal(false);
        setLeadToDelete(null);
        
        // Afficher une notification de succès avec le système de l'application
        notify.success(
          'Lead supprimé',
          `${leadToDelete.prenom} ${leadToDelete.nom} a été supprimé avec succès`
        );
          }
        } catch (error) {
          console.error('Erreur lors de la suppression du lead:', error);
          notify.error(
            'Suppression échouée',
            `Impossible de supprimer le lead: ${error.response?.data?.message || error.message}`
          );
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirmModal(false);
    setLeadToDelete(null);
  };
  
  // Vérifier si l'utilisateur peut modifier tous les champs
  const canEditAllFields = () => {
    return userRole === 'admin' || userRole === 'administrateur' || userRole === 'directeur';
  };
  
  // Vérifier si l'utilisateur peut réassigner un lead
  const canReassignLead = () => {
    return userRole === 'admin' || userRole === 'administrateur';
  };
  
  // Vérifier si l'utilisateur peut assigner des leads
  const canAssignLead = () => {
    return userRole === 'admin' || userRole === 'administrateur' || userRole === 'directeur' || userRole === 'secretaire';
  };
  
  // Vérifier si l'utilisateur peut importer des leads Excel
  const canImportExcel = () => {
    return userRole === 'admin' || userRole === 'administrateur' || userRole === 'directeur' || userRole === 'secretaire';
  };
  
  // Vérifier si l'utilisateur peut distribuer des leads
  const canDispatchLeads = () => {
    return userRole === 'admin' || userRole === 'administrateur' || userRole === 'directeur';
  };
  
  // Ouvrir le modal d'assignation
  const openAssignModal = (id) => {
    setAssignData({
      leadId: id,
      conseillere: ''
    });
    setShowAssignModal(true);
  };
  
  // Assigner un lead à une conseillère
  const assignLead = async (e) => {
    e.preventDefault();
    try {
      const selected = conseillersList.find(c => c.nomComplet === assignData.conseillere);
      if (!selected) {
        notify.error('Conseiller introuvable', 'Le conseiller sélectionné n\'a pas été trouvé');
        return;
      }

      // Vérifier si c'est une réassignation
      const currentLead = leads.find(l => l.id === assignData.leadId);
      const isReassignation = currentLead && currentLead.conseillere && currentLead.conseillere !== assignData.conseillere;

      console.log(`📋 ${isReassignation ? 'Réassignation' : 'Assignation'} du lead ${assignData.leadId} à ${assignData.conseillere}`);

      // Récupérer le token d'authentification
      const token = localStorage.getItem('token');
      if (!token) {
        notify.authError();
        return;
      }

      console.log('🔑 Token utilisé:', token.substring(0, 20) + '...');
      console.log('🎯 Assignation - Lead ID:', assignData.leadId, 'Conseiller ID:', selected.id);

  const response = await axios.post(`${API_BASE}/leads/${assignData.leadId}/assign`, {
        conseiller_id: selected.id
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success) {
        // Mettre à jour l'état local
        const updatedLeads = leads.map(lead => {
          if (lead.id === assignData.leadId) {
            return { ...lead, conseillere: assignData.conseillere, conseiller_id: selected.id };
          }
          return lead;
        });
        setLeads(updatedLeads);
        setShowAssignModal(false);
        
        // Recharger les leads depuis le serveur pour s'assurer de la synchronisation
        await loadLeadsFromAPI();
        
        const actionText = isReassignation ? 'Lead réassigné avec succès' : 'Lead assigné avec succès';
        const notificationText = isReassignation 
          ? 'La nouvelle conseillère a été notifiée de cette réassignation.' 
          : 'La conseillère a été notifiée de cette assignation.';
        
        // 🎯 Notification moderne pour assignation
        notify.success(
          actionText,
          notificationText,
          [{
            label: 'Voir le lead',
            onClick: () => console.log(`Navigation vers lead ${assignData.leadId}`),
            variant: 'primary'
          }]
        );
        console.log(`✅ ${actionText} - notification envoyée`);
      } else {
        console.error('❌ Échec assignation - Réponse serveur:', response.data);
        notify.error(
          'Échec de l\'assignation',
          response.data.message || 'Une erreur inattendue s\'est produite'
        );
      }
    } catch (error) {
      console.error('❌ Erreur lors de l\'assignation:', error);
      console.error('❌ Détails de l\'erreur:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        headers: error.response?.headers
      });
      
      let errorMessage = 'Erreur de communication avec le serveur';
      if (error.response?.status === 403) {
        errorMessage = 'Accès refusé: Vous n\'avez pas les permissions pour assigner des leads';
      } else if (error.response?.status === 401) {
        errorMessage = 'Erreur d\'authentification: Veuillez vous reconnecter';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      notify.error('Erreur d\'assignation', errorMessage);
    }
  };
  
  // Gérer les changements dans le formulaire d'assignation
  const handleAssignChange = (e) => {
    const { name, value } = e.target;
    setAssignData({
      ...assignData,
      [name]: value
    });
  };
  
  // Ouvrir le modal de création de tâche
  const openTaskModal = (lead) => {
    const nomComplet = `${lead.prenom || ''} ${lead.nom || ''}`.trim() || lead.nom_complet || 'Lead';
    setTaskData({
      leadId: lead.id,
      leadNom: nomComplet,
      titre: `Rappeler ${nomComplet}`,
      description: `Contacter le lead ${nomComplet} concernant son intérêt pour nos services`,
      echeance: '',
      priorite: 'important',
      categorie: 'client'
    });
    setShowTaskModal(true);
  };
  
  // Créer une tâche
  const creerTache = (e) => {
    e.preventDefault();
    
    if (!taskData.titre.trim()) {
      notify.error('Erreur', 'Le titre de la tâche est obligatoire');
      return;
    }
    
    if (!taskData.echeance) {
      notify.error('Erreur', 'La date d\'échéance est obligatoire');
      return;
    }
    
    // Créer la tâche avec le hook
    ajouterTache({
      titre: taskData.titre,
      description: taskData.description,
      echeance: taskData.echeance,
      priorite: taskData.priorite,
      categorie: taskData.categorie,
      statut: 'a_faire'
    });
    
    notify.success('Tâche créée', `La tâche "${taskData.titre}" a été créée avec succès`);
    
    setShowTaskModal(false);
    
    // Réinitialiser les données
    setTaskData({
      leadId: null,
      leadNom: '',
      titre: '',
      description: '',
      echeance: '',
      priorite: 'important',
      categorie: 'client'
    });
  };
  
  // Gérer les changements dans le formulaire de tâche
  const handleTaskChange = (e) => {
    const { name, value } = e.target;
    setTaskData({
      ...taskData,
      [name]: value
    });
  };
  
  
  // Gérer le succès d'importation Excel
  const handleImportSuccess = (result) => {
    console.log('✅ Importation réussie:', result);
    
    // Actualiser la liste des leads
    setRefreshTrigger(prev => prev + 1);
    
    // Afficher un message de succès
    let message = `${result.totalImported} leads importés avec succès !`;
    
    if (result.errors && result.errors.length > 0) {
      const errorDetails = result.errors.map(error => `${error.lead}: ${error.error}`).join(', ');
      notify.warning('Importation avec erreurs', `${message}\n${result.errors.length} erreurs: ${errorDetails}`);
    } else {
      notify.success('Importation réussie', message);
    }
  };

  return (
    <div className="leads-container">
      <div className="page-header">
        <h1>Gestion des Leads</h1>
        <div className="header-actions">
          {canBulkReassign && (
            <button
              className="btn btn-secondary"
              onClick={openBulkReassignModal}
              disabled={selectedLeadCount === 0}
              title="Transférer les leads sélectionnés"
            >
              <i className="fas fa-people-arrows"></i> Transférer{selectedLeadCount > 0 ? ` (${selectedLeadCount})` : ''}
            </button>
          )}
          <button 
            className="btn btn-help" 
            onClick={() => setShowProcessHelp(true)}
          >
            <i className="fas fa-question-circle"></i> Aide
          </button>
          <button 
            className="btn btn-primary" 
            onClick={ouvrirModalLead}
          >
            <i className="fas fa-plus"></i> Nouveau Lead
          </button>
          
          {canImportExcel() && (
            <button 
              className="btn btn-secondary" 
              onClick={() => setShowImportModal(true)}
            >
              <i className="fas fa-file-import"></i> Importer Excel
            </button>
          )}
        </div>
      </div>
      
      <div className="search-filter-container" style={{ display: 'flex', alignItems: 'center', gap: '24px', marginBottom: '24px', background: 'white', borderRadius: '12px', boxShadow: 'var(--shadow-sm)', padding: '24px', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', flex: 1, alignItems: 'center', minWidth: 300, height: 40, background: '#f8f9fa', borderRadius: 8, padding: '0 16px', marginRight: 16, marginBottom: 8 }}>
          <i className="fas fa-search" style={{ marginRight: 8, color: 'var(--text-secondary)' }}></i>
          <input 
            type="text" 
            placeholder="Rechercher par nom, email, téléphone, statut, source, conseillère, intérêt ou notes..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ border: 'none', background: 'transparent', outline: 'none', width: '100%', fontSize: '1rem', height: 40, lineHeight: '40px' }}
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap', marginBottom: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, height: 40 }}>
            <label style={{ fontSize: '1rem', color: 'var(--text-secondary)', fontWeight: 500, lineHeight: '40px', marginRight: 4 }}>Statut:</label>
            <select 
              name="statut" 
              value={filtres.statut} 
              onChange={handleFiltreChange}
              style={{ padding: '8px 12px', border: '1px solid var(--color-neutral-medium)', borderRadius: 6, fontSize: '1rem', minWidth: 120, height: 40, boxSizing: 'border-box' }}
            >
              <option value="">Tous</option>
              {statutOptions.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, height: 40 }}>
            <label style={{ fontSize: '1rem', color: 'var(--text-secondary)', fontWeight: 500, lineHeight: '40px', marginRight: 4 }}>Source:</label>
            <select 
              name="source" 
              value={filtres.source} 
              onChange={handleFiltreChange}
              style={{ padding: '8px 12px', border: '1px solid var(--color-neutral-medium)', borderRadius: 6, fontSize: '1rem', minWidth: 120, height: 40, boxSizing: 'border-box' }}
            >
              <option value="">Toutes</option>
              {sourceOptions.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, height: 40 }}>
            <label style={{ fontSize: '1rem', color: 'var(--text-secondary)', fontWeight: 500, lineHeight: '40px', marginRight: 4 }}>Assigné(e) à :</label>
            <select 
              name="conseillere" 
              value={filtres.conseillere} 
              onChange={handleFiltreChange}
              disabled={userRole === 'conseillere'}
              style={{
                backgroundColor: userRole === 'conseillere' ? '#f5f5f5' : 'white',
                color: userRole === 'conseillere' ? '#999' : 'black',
                cursor: userRole === 'conseillere' ? 'not-allowed' : 'pointer',
                padding: '8px 12px',
                border: '1px solid var(--color-neutral-medium)',
                borderRadius: 6,
                fontSize: '1rem',
                minWidth: 120,
                height: 40,
                boxSizing: 'border-box'
              }}
            >
              <option value="">
                {userRole === 'conseillere' ? 
                  (nomConseillerConnectee ? `${nomConseillerConnectee}` : 'Mes leads') : 
                  'Toutes'
                }
              </option>
              {userRole !== 'conseillere' && conseilleresOptions.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>
          <button className="btn-text" onClick={resetFiltres} style={{ height: 40, boxSizing: 'border-box', display: 'flex', alignItems: 'center', gap: 4, fontSize: '1rem', padding: '8px 12px', borderRadius: 6, marginLeft: 8, whiteSpace: 'nowrap' }}>
            <i className="fas fa-times"></i> Réinitialiser
          </button>
        </div>
      </div>

      {/* Sélecteur de source des leads */}
      <SourceSelector 
        currentSource={currentLeadSource}
        onSourceChange={setCurrentLeadSource}
        userRole={userRole}
      />

      {/* Manager Google Sheets pour les admins */}
      {(userRole === 'admin' || userRole === 'super_admin' || userRole === 'administrateur') && showGoogleSheetsManager && (
        <GoogleSheetsManager
          isOpen={showGoogleSheetsManager}
          onClose={() => setShowGoogleSheetsManager(false)}
          onConfigUpdate={(config) => {
            console.log('🔧 Configuration Google Sheets mise à jour:', config);
            // Optionnel: actualiser les sources disponibles après configuration
          }}
          userRole={userRole}
        />
      )}

      {/* Bouton pour ouvrir le manager Google Sheets */}
      {(userRole === 'admin' || userRole === 'super_admin' || userRole === 'administrateur') && (
        <div style={{ marginBottom: '20px', textAlign: 'right' }}>
          <button 
            className="btn btn-secondary"
            onClick={() => setShowGoogleSheetsManager(true)}
            style={{ fontSize: '14px' }}
          >
            <i className="fas fa-cog"></i> Configurer Google Sheets
          </button>
        </div>
      )}
      
      {/* Pagination en haut */}
      {totalPages > 1 && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '10px',
          marginBottom: '20px',
          padding: '15px 0',
          background: 'white',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <span style={{
            color: '#6c757d',
            fontSize: '14px',
            marginRight: '15px'
          }}>
            Page {currentPage} sur {totalPages}
          </span>
          
          <button
            onClick={goToPreviousPage}
            disabled={currentPage === 1}
            style={{
              padding: '6px 12px',
              border: '1px solid #dee2e6',
              borderRadius: '4px',
              backgroundColor: currentPage === 1 ? '#f8f9fa' : 'white',
              color: currentPage === 1 ? '#6c757d' : '#495057',
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease',
              fontSize: '12px'
            }}
          >
            ← Préc
          </button>
          
          <div style={{ display: 'flex', gap: '3px' }}>
            {Array.from({ length: Math.min(totalPages, 7) }, (_, index) => {
              let page;
              if (totalPages <= 7) {
                page = index + 1;
              } else if (currentPage <= 4) {
                page = index + 1;
              } else if (currentPage >= totalPages - 3) {
                page = totalPages - 6 + index;
              } else {
                page = currentPage - 3 + index;
              }
              
              return (
                <button
                  key={page}
                  onClick={() => goToPage(page)}
                  style={{
                    padding: '6px 10px',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px',
                    backgroundColor: page === currentPage ? '#007bff' : 'white',
                    color: page === currentPage ? 'white' : '#495057',
                    cursor: 'pointer',
                    fontWeight: page === currentPage ? 'bold' : 'normal',
                    transition: 'all 0.2s ease',
                    minWidth: '32px',
                    fontSize: '12px'
                  }}
                >
                  {page}
                </button>
              );
            })}
          </div>
          
          <button
            onClick={goToNextPage}
            disabled={currentPage === totalPages}
            style={{
              padding: '6px 12px',
              border: '1px solid #dee2e6',
              borderRadius: '4px',
              backgroundColor: currentPage === totalPages ? '#f8f9fa' : 'white',
              color: currentPage === totalPages ? '#6c757d' : '#495057',
              cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease',
              fontSize: '12px'
            }}
          >
            Suiv →
          </button>
          
          <span style={{
            color: '#6c757d',
            fontSize: '12px',
            marginLeft: '15px'
          }}>
            {leadsFiltrés.length} leads
          </span>
        </div>
      )}
      
      <div className="leads-table-container">
        <table className="leads-table sortable-table">
          <thead>
            <tr>
              {canBulkReassign && (
                <th className="lead-selection-cell" title="Sélectionner tous les leads correspondant aux filtres">
                  <input
                    type="checkbox"
                    checked={allFilteredLeadsSelected}
                    onChange={toggleAllFilteredLeads}
                    aria-label="Sélectionner tous les leads filtrés"
                  />
                </th>
              )}
              <th 
                className={`sortable-header ${sortField === 'nom' ? 'active' : ''}`}
                onClick={() => handleSort('nom')}
                title="Cliquer pour trier"
              >
                Nom complet <span className="sort-indicator">{getSortIcon('nom')}</span>
              </th>
              <th 
                className={`sortable-header ${sortField === 'email' ? 'active' : ''}`}
                onClick={() => handleSort('email')}
                title="Cliquer pour trier"
              >
                Email <span className="sort-indicator">{getSortIcon('email')}</span>
              </th>
              <th 
                className={`sortable-header ${sortField === 'telephone' ? 'active' : ''}`}
                onClick={() => handleSort('telephone')}
                title="Cliquer pour trier"
              >
                Téléphone <span className="sort-indicator">{getSortIcon('telephone')}</span>
              </th>
              <th 
                className={`sortable-header ${sortField === 'source' ? 'active' : ''}`}
                onClick={() => handleSort('source')}
                title="Cliquer pour trier"
              >
                Source <span className="sort-indicator">{getSortIcon('source')}</span>
              </th>
              <th 
                className={`sortable-header ${sortField === 'statut' ? 'active' : ''}`}
                onClick={() => handleSort('statut')}
                title="Cliquer pour trier"
              >
                Statut <span className="sort-indicator">{getSortIcon('statut')}</span>
              </th>
              <th 
                className={`sortable-header ${sortField === 'dateCreation' ? 'active' : ''}`}
                onClick={() => handleSort('dateCreation')}
                title="Cliquer pour trier"
              >
                Date création <span className="sort-indicator">{getSortIcon('dateCreation')}</span>
              </th>
              <th 
                className={`sortable-header ${sortField === 'conseillere' ? 'active' : ''}`}
                onClick={() => handleSort('conseillere')}
                title="Cliquer pour trier"
              >
                Conseillère <span className="sort-indicator">{getSortIcon('conseillere')}</span>
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoadingLeads ? (
              <tr>
                <td colSpan={canBulkReassign ? 9 : 8} className="loading-state" style={{ textAlign: 'center', padding: '40px' }}>
                  <i className="fas fa-spinner fa-spin" style={{ fontSize: '24px', color: '#007bff' }}></i>
                  <p style={{ margin: '10px 0 0 0' }}>Chargement des leads depuis l'API backend...</p>
                </td>
              </tr>
            ) : leadsPaginés.length > 0 ? (
              leadsPaginés.map(lead => (
                <tr key={lead.id}>
                  {canBulkReassign && (
                    <td className="lead-selection-cell" onClick={(event) => event.stopPropagation()}>
                      {lead.statut === 'Fermé' ? (
                        <span title="Un lead fermé ne peut pas être transféré">—</span>
                      ) : (
                        <input
                          type="checkbox"
                          checked={selectedLeadIds.includes(lead.id)}
                          onChange={() => toggleLeadSelection(lead.id)}
                          aria-label={`Sélectionner ${lead.prenom} ${lead.nom}`}
                        />
                      )}
                    </td>
                  )}
                  <td className="clickable" onClick={() => openLeadDetail(lead)}>
                    {lead.prenom} {lead.nom}
                  </td>
                  <td>{lead.email}</td>
                  <td>{lead.telephone}</td>
                  <td>{lead.source}</td>
                  <td>
                    <span className={`status-badge status-${lead.statut.toLowerCase().replace(/\s+/g, '-').replace(/[éèê]/g, 'e')} ${lead.statut === 'Client' ? 'locked' : ''}`}>
                      <i className={`fas ${getStatusIcon(lead.statut)}`}></i>
                      {lead.statut}
                    </span>
                  </td>
                  <td>{lead.dateCreation}</td>
                  <td>
                    {lead.conseillere || (lead.statut !== 'Fermé' && (
                      <button 
                        className="btn-assign"
                        onClick={() => openAssignModal(lead.id)}
                        disabled={userRole !== 'secretaire' && userRole !== 'administrateur' && userRole !== 'directeur'}
                      >
                        <i className="fas fa-user-plus"></i> Assigner
                      </button>
                    ))}
                  </td>
                  <td>
                    <div className="actions-dropdown">
                      <button 
                        className="btn-action actions-dropdown-button"
                        onClick={(e) => toggleDropdown(lead.id, e)}
                        type="button"
                      >
                        <i className="fas fa-ellipsis-v"></i>
                      </button>
                      {openDropdown === lead.id && (
                        <PortalDropdown
                          anchorEl={dropdownAnchorEl}
                          onClose={() => { setOpenDropdown(null); setDropdownAnchorEl(null); }}
                          align="end"
                          offset={6}
                          autoFlip={true}
                          preferDown={true}
                        >
                          <div className="dropdown-content actions-dropdown-menu show">
                            <button className="actions-dropdown-item" onClick={(e) => {
                              e.stopPropagation();
                              openLeadDetail(lead);
                              setTimeout(() => setOpenDropdown(null), 100);
                            }}>
                              <i className="fas fa-eye"></i> Voir détails
                            </button>
                            
                            {/* Actions de changement de statut - masquées pour les clients convertis */}
                            {lead.statut !== 'Client' && lead.statut !== 'Fermé' && (
                              <>
                                <button className="actions-dropdown-item" onClick={(e) => {
                                  e.stopPropagation();
                                  changerStatut(lead.id, 'Contacté');
                                  setTimeout(() => setOpenDropdown(null), 100);
                                }}>
                                  <i className="fas fa-phone"></i> Marquer comme contacté
                                </button>
                                <button className="actions-dropdown-item" onClick={(e) => {
                                  e.stopPropagation();
                                  openTaskModal(lead);
                                  setTimeout(() => setOpenDropdown(null), 100);
                                }}>
                                  <i className="fas fa-clock"></i> Planifier un rappel
                                </button>
                                <button className="actions-dropdown-item" onClick={(e) => {
                                  e.stopPropagation();
                                  // Ouvrir le modal de rendez-vous directement
                                  setSelectedLeadForRendezVous(lead);
                                  setNouveauRdv({
                                    client: `${lead.prenom || ''} ${lead.nom || ''}`.trim() || lead.nom_complet || '',
                                    client_id: lead.id,
                                    client_type: 'lead',
                                    conseillere: userRole === 'conseillere' ? userName : '',
                                    date: '',
                                    heureDebut: '',
                                    heureFin: '',
                                    type: '',
                                    notes: ''
                                  });
                                  setShowRendezVousModal(true);
                                  setTimeout(() => setOpenDropdown(null), 100);
                                }}>
                                  <i className="fas fa-calendar-plus"></i> Prendre rendez-vous
                                </button>
                                <button className="actions-dropdown-item" onClick={(e) => {
                                  e.stopPropagation();
                                  changerStatut(lead.id, 'Injoignable à rappeler');
                                  setTimeout(() => setOpenDropdown(null), 100);
                                }}>
                                  <i className="fas fa-phone-slash"></i> Injoignable à rappeler
                                </button>
                                <button className="actions-dropdown-item" onClick={(e) => {
                                  e.stopPropagation();
                                  changerStatut(lead.id, 'Qualifié');
                                  setTimeout(() => setOpenDropdown(null), 100);
                                }}>
                                  <i className="fas fa-user-check"></i> Qualifier
                                </button>
                                <button className="actions-dropdown-item" onClick={(e) => {
                                  e.stopPropagation();
                                  changerStatut(lead.id, 'Pas intéressé');
                                  setTimeout(() => setOpenDropdown(null), 100);
                                }}>
                                  <i className="fas fa-user-times"></i> Pas intéressé
                                </button>
                                <button className="actions-dropdown-item danger" onClick={(e) => {
                                  e.stopPropagation();
                                  openCloseLeadModal(lead);
                                }}>
                                  <i className="fas fa-lock"></i> Fermer le lead
                                </button>
                                <button className="actions-dropdown-item" onClick={(e) => {
                                  e.stopPropagation();
                                  openConvertModal(lead);
                                  setTimeout(() => setOpenDropdown(null), 100);
                                }}>
                                  <i className="fas fa-user-tie"></i> Convertir en client
                                </button>
                              </>
                            )}
                            
                            {/* Message informatif pour les clients convertis */}
                            {lead.statut === 'Client' && (
                              <div className="dropdown-info">
                                <i className="fas fa-lock"></i> Lead converti en client
                                <small>Le statut ne peut plus être modifié</small>
                              </div>
                            )}

                            {lead.statut === 'Fermé' && (
                              <>
                                <div className="dropdown-info">
                                  <i className="fas fa-lock"></i> Lead fermé
                                  <small>{lead.motif_fermeture || 'Motif non renseigné'}</small>
                                </div>
                                <button className="actions-dropdown-item" onClick={(e) => {
                                  e.stopPropagation();
                                  changerStatut(lead.id, 'Nouveau');
                                  setTimeout(() => setOpenDropdown(null), 100);
                                }}>
                                  <i className="fas fa-lock-open"></i> Réouvrir le lead
                                </button>
                              </>
                            )}
                            
                            {(userRole === 'administrateur' || userRole === 'directeur') && (
                              <button className="actions-dropdown-item danger" onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteClick(lead.id);
                                setTimeout(() => setOpenDropdown(null), 100);
                              }}>
                                <i className="fas fa-trash"></i> Supprimer
                              </button>
                            )}
                          </div>
                        </PortalDropdown>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={canBulkReassign ? 9 : 8} className="no-results">Aucun lead ne correspond aux critères de recherche</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      
      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '10px',
          marginTop: '20px',
          padding: '20px 0'
        }}>
          <button
            onClick={goToPreviousPage}
            disabled={currentPage === 1}
            style={{
              padding: '8px 16px',
              border: '1px solid #dee2e6',
              borderRadius: '4px',
              backgroundColor: currentPage === 1 ? '#f8f9fa' : 'white',
              color: currentPage === 1 ? '#6c757d' : '#495057',
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            ← Précédent
          </button>
          
          <div style={{ display: 'flex', gap: '5px' }}>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, index) => {
              let page;
              if (totalPages <= 5) {
                page = index + 1;
              } else if (currentPage <= 3) {
                page = index + 1;
              } else if (currentPage >= totalPages - 2) {
                page = totalPages - 4 + index;
              } else {
                page = currentPage - 2 + index;
              }
              
              return (
                <button
                  key={page}
                  onClick={() => goToPage(page)}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px',
                    backgroundColor: page === currentPage ? '#007bff' : 'white',
                    color: page === currentPage ? 'white' : '#495057',
                    cursor: 'pointer',
                    fontWeight: page === currentPage ? 'bold' : 'normal',
                    transition: 'all 0.2s ease',
                    minWidth: '40px'
                  }}
                >
                  {page}
                </button>
              );
            })}
          </div>
          
          <button
            onClick={goToNextPage}
            disabled={currentPage === totalPages}
            style={{
              padding: '8px 16px',
              border: '1px solid #dee2e6',
              borderRadius: '4px',
              backgroundColor: currentPage === totalPages ? '#f8f9fa' : 'white',
              color: currentPage === totalPages ? '#6c757d' : '#495057',
              cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            Suivant →
          </button>
        </div>
      )}
      
      {/* Informations de pagination */}
      <div style={{
        textAlign: 'center',
        color: '#6c757d',
        fontSize: '14px',
        marginTop: '10px'
      }}>
        Affichage {((currentPage - 1) * leadsPerPage) + 1} à {Math.min(currentPage * leadsPerPage, leadsFiltrés.length)} sur {leadsFiltrés.length} leads
      </div>
      
      {/* Modal d'aide sur le processus */}
      {showProcessHelp && <LeadProcessHelp onClose={() => setShowProcessHelp(false)} />}

      {/* Répartition équilibrée entre les collaboratrices choisies. */}
      {showBulkReassignModal && (
        <div className="modal-overlay" onClick={() => !isBulkReassigning && setShowBulkReassignModal(false)}>
          <div className="modal-content bulk-reassign-modal" onClick={(event) => event.stopPropagation()}>
            <div className="modal-header">
              <h3><i className="fas fa-people-arrows"></i> Transférer {selectedLeadCount} lead{selectedLeadCount > 1 ? 's' : ''}</h3>
              <button className="close-btn" onClick={() => setShowBulkReassignModal(false)} disabled={isBulkReassigning}>×</button>
            </div>
            <div className="bulk-reassign-content">
              <p className="bulk-reassign-intro">Choisissez une ou plusieurs conseillères ou secrétaires. Si vous en choisissez plusieurs, les leads seront répartis équitablement entre elles.</p>
              {assignableDestinataires.length > 0 ? (
                <div className="bulk-conseillers-list">
                  {assignableDestinataires.map((conseiller) => (
                    <label className="bulk-conseiller-option" key={conseiller.id}>
                      <input type="checkbox" checked={bulkConseillerIds.includes(conseiller.id)} onChange={() => toggleBulkConseiller(conseiller.id)} disabled={isBulkReassigning} />
                      <span><strong>{conseiller.nomComplet}</strong><small>{conseiller.role === 'secretaire' ? 'Secrétaire' : 'Conseillère'} · {conseiller.email}</small></span>
                    </label>
                  ))}
                </div>
              ) : (
                <p className="bulk-reassign-empty">Aucune conseillère ou secrétaire active ne peut actuellement recevoir de leads.</p>
              )}
              {bulkConseillerIds.length > 0 && (
                <p className="bulk-reassign-preview">Répartition prévue : environ {Math.floor(selectedLeadCount / bulkConseillerIds.length)} à {Math.ceil(selectedLeadCount / bulkConseillerIds.length)} lead{selectedLeadCount > 1 ? 's' : ''} par personne.</p>
              )}
            </div>
            <div className="form-actions">
              <button type="button" className="btn-secondary" onClick={() => setShowBulkReassignModal(false)} disabled={isBulkReassigning}>Annuler</button>
              <button type="button" className="btn-primary" onClick={handleBulkReassign} disabled={isBulkReassigning || bulkConseillerIds.length === 0}>
                <i className={`fas ${isBulkReassigning ? 'fa-spinner fa-spin' : 'fa-check'}`}></i>{isBulkReassigning ? 'Transfert en cours…' : 'Confirmer le transfert'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal pour ajouter un nouveau lead */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Ajouter un nouveau lead</h3>
              <button className="close-btn" onClick={fermerModalLead}>×</button>
            </div>
            <form onSubmit={ajouterLead}>
              <div className="form-row">
                <div className="form-group">
                  <label>Nom:</label>
                  <input 
                    type="text" 
                    name="nom" 
                    value={nouveauLead.nom} 
                    onChange={handleNouveauLeadChange} 
                    required 
                  />
                </div>
                <div className="form-group">
                  <label>Prénom:</label>
                  <input 
                    type="text" 
                    name="prenom" 
                    value={nouveauLead.prenom} 
                    onChange={handleNouveauLeadChange} 
                    required 
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Email:</label>
                  <input 
                    type="email" 
                    name="email" 
                    value={nouveauLead.email} 
                    onChange={handleNouveauLeadChange} 
                    required 
                    className={emailError ? 'input-error' : ''}
                  />
                  {emailError && (
                    <div className="error-message" style={{ color: '#dc3545', fontSize: '12px', marginTop: '5px' }}>
                      <i className="fas fa-exclamation-circle"></i> {emailError}
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label>Téléphone:</label>
                  <input 
                    type="tel" 
                    name="telephone" 
                    value={nouveauLead.telephone} 
                    onChange={handleNouveauLeadChange} 
                    required 
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Source:</label>
                  <select 
                    name="source" 
                    value={nouveauLead.source} 
                    onChange={handleNouveauLeadChange}
                  >
                    {sourceOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Intérêt principal:</label>
                  <select 
                    name="interet" 
                    value={nouveauLead.interet} 
                    onChange={handleNouveauLeadChange}
                    required
                  >
                    <option value="">Sélectionner</option>
                    {interetOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              {/* Champ conditionnel pour les détails de la référence */}
              {nouveauLead.source === 'Référence' && (
                <div className="form-row reference-details-field">
                  <div className="form-group full-width conditional-field">
                    <label>Détails de la référence <span className="required">*</span>:</label>
                    <input
                      type="text"
                      name="referenceDetails"
                      value={nouveauLead.referenceDetails}
                      onChange={handleNouveauLeadChange}
                      placeholder="Qui vous a référé ? (nom, entreprise, relation...)"
                      required={nouveauLead.source === 'Référence'}
                    />
                  </div>
                </div>
              )}
              
              <div className="form-row">
                <div className="form-group">
                  <label>Conseillère:</label>
                  {userRole === 'conseillere' ? (
                    <input
                      type="text"
                      name="conseillere"
                      value={nomConseillerConnectee || nouveauLead.conseillere}
                      readOnly
                      className="form-control"
                    />
                  ) : (
                    <select 
                      name="conseillere" 
                      value={nouveauLead.conseillere} 
                      onChange={handleNouveauLeadChange}
                    >
                      <option value="">À assigner</option>
                      {(userRole === 'conseillere' 
                        ? [userName] 
                        : conseilleresOptions
                      ).map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  )}
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group full-width">
                  <label>Notes:</label>
                  <textarea 
                    name="notes" 
                    value={nouveauLead.notes} 
                    onChange={handleNouveauLeadChange}
                    rows="3"
                  ></textarea>
                </div>
              </div>
              
              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={fermerModalLead}>
                  Annuler
                </button>
                <button type="submit" className="btn-primary">
                  Ajouter
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Modal pour importer des leads depuis Excel */}
      <ImportExcelModal 
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImportSuccess={handleImportSuccess}
        conseillers={conseillersList}
      />
      
      {/* Modal pour la vue détaillée d'un lead */}
      {showDetailModal && selectedLead && (
        <div className="modal-overlay">
          <div className="modal-content modal-large">
            <div className="modal-header">
              <h3>Détails du lead: {selectedLead.prenom} {selectedLead.nom}</h3>
              <button className="close-btn" onClick={() => setShowDetailModal(false)}>×</button>
            </div>
            <form onSubmit={updateLead}>
              {/* Message informatif pour les leads convertis */}
              {selectedLead._originalStatut === 'Client' && (
                <div className="modal-info-message">
                  <i className="fas fa-info-circle"></i>
                  Ce lead a été converti en client. Certaines modifications sont restreintes pour préserver l'intégrité des données.
                </div>
              )}
              {/* Message informatif pour les leads en cours de conversion */}
              {selectedLead.statut === 'Client' && selectedLead._originalStatut !== 'Client' && (
                <div className="modal-info-message" style={{ backgroundColor: '#fff3cd', borderColor: '#ffc107', color: '#856404' }}>
                  <i className="fas fa-exclamation-triangle"></i>
                  Ce lead va être converti en client. Certaines modifications sont restreintes pour préserver l'intégrité des données.
                </div>
              )}
              
              <div className="form-row">
                <div className="form-group">
                  <label>Nom:</label>
                  <input 
                    type="text" 
                    name="nom" 
                    value={selectedLead.nom} 
                    onChange={handleDetailChange} 
                    required 
                  />
                </div>
                <div className="form-group">
                  <label>Prénom:</label>
                  <input 
                    type="text" 
                    name="prenom" 
                    value={selectedLead.prenom} 
                    onChange={handleDetailChange} 
                    required 
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Email:</label>
                  <input 
                    type="email" 
                    name="email" 
                    value={selectedLead.email} 
                    onChange={handleDetailChange} 
                    required 
                  />
                </div>
                <div className="form-group">
                  <label>Téléphone:</label>
                  <input 
                    type="tel" 
                    name="telephone" 
                    value={selectedLead.telephone} 
                    onChange={handleDetailChange} 
                    required 
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Source:</label>
                  <select 
                    name="source" 
                    value={selectedLead.source} 
                    onChange={handleDetailChange}
                  >
                    {sourceOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Statut:</label>
                  <select 
                    name="statut" 
                    value={selectedLead.statut} 
                    onChange={handleDetailChange}
                    disabled={selectedLead._originalStatut === 'Client'}
                    style={{
                      backgroundColor: selectedLead._originalStatut === 'Client' ? '#f8f9fa' : 'white',
                      cursor: selectedLead._originalStatut === 'Client' ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {statutOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                  {selectedLead._originalStatut === 'Client' && (
                    <small style={{ color: '#6c757d', display: 'block', marginTop: '5px' }}>
                      <i className="fas fa-lock"></i> Le statut ne peut plus être modifié car le lead a été converti en client
                    </small>
                  )}
                </div>
              </div>

              {selectedLead.statut === 'Fermé' && (
                <div className="form-row">
                  <div className="form-group full-width">
                    <label>Motif de fermeture :</label>
                    <select
                      name="motif_fermeture"
                      value={selectedLead.motif_fermeture || ''}
                      onChange={handleDetailChange}
                      required
                    >
                      <option value="">Sélectionner un motif</option>
                      {CLOSURE_REASONS.map((reason) => (
                        <option key={reason} value={reason}>{reason}</option>
                      ))}
                    </select>
                    <small>Ce lead sera conservé dans l'historique, mais exclu des relances et des transferts.</small>
                  </div>
                </div>
              )}

              {/* Champ conditionnel pour les détails de référence */}
              {selectedLead.source === 'Référence' && (
                <div className="form-row">
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label>Détails de la référence:</label>
                    <textarea 
                      name="reference_details" 
                      value={selectedLead.reference_details || ''} 
                      onChange={handleDetailChange}
                      placeholder="Veuillez fournir les détails de la référence (qui vous a référé, comment, etc.)"
                      rows="3"
                      style={{
                        resize: 'vertical',
                        minHeight: '80px'
                      }}
                    />
                  </div>
                </div>
              )}
              
              <div className="form-row">
                <div className="form-group">
                  <label>Intérêt principal:</label>
                  <select 
                    name="interet" 
                    value={selectedLead.interet} 
                    onChange={handleDetailChange}
                    required
                  >
                    <option value="">Sélectionner</option>
                    {interetOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Conseillère:</label>
                  <select 
                    name="conseillere" 
                    value={selectedLead.conseillere} 
                    onChange={handleDetailChange}
                    disabled={userRole === 'conseillere'}
                  >
                    <option value="">À assigner</option>
                    {(userRole === 'conseillere' 
                      ? [userName] 
                      : conseilleresOptions
                    ).map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Date de création:</label>
                  <input 
                    type="text" 
                    value={selectedLead.dateCreation} 
                    readOnly 
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group full-width">
                  <label>Notes:</label>
                  <textarea 
                    name="notes" 
                    value={selectedLead.notes} 
                    onChange={handleDetailChange}
                    rows="5"
                  ></textarea>
                </div>
              </div>
              
              {/* Gestionnaire de consultations */}
              <ConsultationManager 
                leadId={selectedLead.id}
                conseillerId={(() => {
                  // Méthode robuste pour trouver l'ID du conseiller
                  let conseiller = null;
                  
                  // 1. Recherche exacte par nomComplet
                  conseiller = conseillersList.find(c => c.nomComplet === selectedLead.conseillere);
                  
                  // 2. Recherche par userId pour les conseillères connectées
                  if (!conseiller && userRole === 'conseillere') {
                    const userId = localStorage.getItem('userId');
                    if (userId) {
                      conseiller = conseillersList.find(c => c.id === parseInt(userId));
                    }
                  }
                  
                  // 3. Recherche par nom partiel
                  if (!conseiller && selectedLead.conseillere) {
                    const [prenom, ...nomParts] = selectedLead.conseillere.split(' ');
                    const nom = nomParts.join(' ');
                    conseiller = conseillersList.find(c => 
                      c.prenom === prenom && c.nom === nom
                    );
                  }
                  
                  console.log('🔍 ConsultationManager - Conseiller trouvé:', conseiller?.id);
                  return conseiller?.id;
                })()}
                onConsultationChange={(action, consultation) => {
                  console.log(`Consultation ${action}:`, consultation);
                  // Optionnel : actualiser les données si nécessaire
                }}
              />
              
              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowDetailModal(false)}>
                  Fermer
                </button>
                <button type="submit" className="btn-primary">
                  Enregistrer les modifications
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Modal pour assigner un lead à une conseillère */}
      {showAssignModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Assigner le lead à une conseillère</h3>
              <button className="close-btn" onClick={() => setShowAssignModal(false)}>×</button>
            </div>
            <form onSubmit={assignLead}>
              <div className="form-row">
                <div className="form-group full-width">
                  <label>Conseillère:</label>
                  <select 
                    name="conseillere" 
                    value={assignData.conseillere} 
                    onChange={handleAssignChange}
                    required
                    disabled={userRole === 'conseillere'}
                  >
                    <option value="">Sélectionner</option>
                    {(userRole === 'conseillere' 
                      ? [userName] 
                      : conseilleresOptions
                    ).map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowAssignModal(false)}>
                  Annuler
                </button>
                <button type="submit" className="btn-primary">
                  Assigner
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Modal pour créer une tâche */}
      {showTaskModal && (
        <div className="modal-overlay" onClick={() => setShowTaskModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h3>Créer une tâche - {taskData.leadNom}</h3>
              <button className="close-btn" onClick={() => setShowTaskModal(false)}>×</button>
            </div>
            <form onSubmit={creerTache}>
              <div className="form-row">
                <div className="form-group full-width">
                  <label>Titre de la tâche <span style={{ color: '#e74c3c' }}>*</span></label>
                  <input 
                    type="text" 
                    name="titre" 
                    value={taskData.titre} 
                    onChange={handleTaskChange} 
                    placeholder="Ex: Rappeler le lead"
                    required 
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group full-width">
                  <label>Description</label>
                  <textarea 
                    name="description" 
                    value={taskData.description} 
                    onChange={handleTaskChange}
                    rows="4"
                    placeholder="Détails de la tâche..."
                  ></textarea>
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Date et heure d'échéance <span style={{ color: '#e74c3c' }}>*</span></label>
                  <input 
                    type="datetime-local" 
                    name="echeance" 
                    value={taskData.echeance} 
                    onChange={handleTaskChange} 
                    required 
                  />
                </div>
                <div className="form-group">
                  <label>Priorité</label>
                  <select 
                    name="priorite" 
                    value={taskData.priorite} 
                    onChange={handleTaskChange}
                  >
                    <option value="normal">Normal</option>
                    <option value="important">Important</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Catégorie</label>
                  <select 
                    name="categorie" 
                    value={taskData.categorie} 
                    onChange={handleTaskChange}
                  >
                    <option value="client">Client</option>
                    <option value="dossier">Dossier</option>
                    <option value="admin">Administration</option>
                    <option value="personnel">Personnel</option>
                  </select>
                </div>
              </div>
              
              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowTaskModal(false)}>
                  Annuler
                </button>
                <button type="submit" className="btn-primary">
                  <i className="fas fa-check"></i> Créer la tâche
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Modal de confirmation de changement de statut */}
      {showStatutChangeModal && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
            backdropFilter: 'blur(4px)'
          }}
          onClick={() => {
            setShowStatutChangeModal(false);
            setStatutChangeData({ oldStatut: '', newStatut: '', commentaire: '' });
          }}
        >
          <div 
            style={{
              backgroundColor: '#1e293b',
              borderRadius: '16px',
              padding: '32px',
              maxWidth: '550px',
              width: '90%',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
              border: '1px solid rgba(251, 191, 36, 0.3)',
              animation: 'slideIn 0.3s ease-out'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Icône d'avertissement */}
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <div style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                backgroundColor: 'rgba(251, 191, 36, 0.15)',
                margin: '0 auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '16px'
              }}>
                <span style={{ fontSize: '48px' }}>⚠️</span>
              </div>
              <h3 style={{
                color: '#fbbf24',
                fontSize: '24px',
                fontWeight: '700',
                margin: '0 0 8px 0'
              }}>
                ATTENTION
              </h3>
              <p style={{
                color: '#94a3b8',
                fontSize: '14px',
                margin: 0
              }}>
                Vous modifiez le statut d'une consultation effectuée
              </p>
            </div>
            
            {/* Informations sur le changement */}
            <div style={{
              backgroundColor: 'rgba(251, 191, 36, 0.1)',
              borderRadius: '12px',
              padding: '20px',
              marginBottom: '24px',
              border: '1px solid rgba(251, 191, 36, 0.2)'
            }}>
              <div style={{ marginBottom: '12px' }}>
                <label style={{
                  color: '#94a3b8',
                  fontSize: '12px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  display: 'block',
                  marginBottom: '4px'
                }}>
                  Statut actuel
                </label>
                <div style={{
                  color: '#10b981',
                  fontSize: '16px',
                  fontWeight: '600'
                }}>
                  "{statutChangeData.oldStatut}"
                </div>
              </div>
              
              <div style={{
                width: '100%',
                height: '2px',
                background: 'linear-gradient(to right, transparent, rgba(251, 191, 36, 0.3), transparent)',
                margin: '12px 0'
              }}></div>
              
              <div>
                <label style={{
                  color: '#94a3b8',
                  fontSize: '12px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  display: 'block',
                  marginBottom: '4px'
                }}>
                  Nouveau statut
                </label>
                <div style={{
                  color: '#fbbf24',
                  fontSize: '16px',
                  fontWeight: '600'
                }}>
                  "{statutChangeData.newStatut}"
                </div>
              </div>
            </div>
            
            {/* Champ de commentaire */}
            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block',
                color: '#e2e8f0',
                fontSize: '14px',
                fontWeight: '600',
                marginBottom: '12px'
              }}>
                Veuillez expliquer la raison de ce changement :
                <span style={{ color: '#ef4444', marginLeft: '4px' }}>*</span>
              </label>
              <textarea
                value={statutChangeData.commentaire}
                onChange={(e) => setStatutChangeData({
                  ...statutChangeData,
                  commentaire: e.target.value
                })}
                placeholder="Entrez votre justification ici..."
                style={{
                  width: '100%',
                  minHeight: '120px',
                  padding: '14px',
                  backgroundColor: '#0f172a',
                  border: '2px solid rgba(251, 191, 36, 0.3)',
                  borderRadius: '8px',
                  color: '#e2e8f0',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  resize: 'vertical',
                  outline: 'none',
                  transition: 'border-color 0.2s'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#fbbf24';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'rgba(251, 191, 36, 0.3)';
                }}
                autoFocus
              />
            </div>
            
            {/* Boutons d'action */}
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => {
                  setShowStatutChangeModal(false);
                  setStatutChangeData({ oldStatut: '', newStatut: '', commentaire: '' });
                }}
                style={{
                  padding: '12px 24px',
                  backgroundColor: 'transparent',
                  border: '2px solid #475569',
                  borderRadius: '8px',
                  color: '#cbd5e1',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#475569';
                  e.target.style.color = '#fff';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = '#cbd5e1';
                }}
              >
                Annuler
              </button>
              <button
                onClick={handleConfirmStatutChange}
                style={{
                  padding: '12px 32px',
                  backgroundColor: '#fbbf24',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#1e293b',
                  fontSize: '14px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  boxShadow: '0 4px 12px rgba(251, 191, 36, 0.3)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#f59e0b';
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 16px rgba(251, 191, 36, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#fbbf24';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 12px rgba(251, 191, 36, 0.3)';
                }}
              >
                Confirmer
              </button>
            </div>
          </div>
        </div>
      )}

      {showCloseLeadModal && leadToClose && (
        <div className="modal-overlay" onClick={() => setShowCloseLeadModal(false)}>
          <div className="modal-content" style={{ maxWidth: '520px' }} onClick={(event) => event.stopPropagation()}>
            <div className="modal-header">
              <h3><i className="fas fa-lock"></i> Fermer le lead</h3>
              <button className="close-btn" onClick={() => setShowCloseLeadModal(false)}>×</button>
            </div>
            <div className="form-group full-width" style={{ padding: '0 4px' }}>
              <p>
                {leadToClose.prenom} {leadToClose.nom} ne recevra plus de relances et ne pourra plus être réassigné.
              </p>
              <label>Motif de fermeture <span style={{ color: '#dc3545' }}>*</span></label>
              <select value={closureReason} onChange={(event) => setClosureReason(event.target.value)} required>
                <option value="">Sélectionner un motif</option>
                {CLOSURE_REASONS.map((reason) => (
                  <option key={reason} value={reason}>{reason}</option>
                ))}
              </select>
            </div>
            <div className="form-actions">
              <button type="button" className="btn-secondary" onClick={() => setShowCloseLeadModal(false)}>Annuler</button>
              <button type="button" className="btn-danger" onClick={closeLead}>
                <i className="fas fa-lock"></i> Fermer le lead
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal pour convertir un lead en client */}
      {selectedLeadForConversion && (
        <ConvertLeadModal
          lead={selectedLeadForConversion}
          isOpen={showConvertModal}
          onClose={closeConvertModal}
          onConvert={handleConvertLead}
          currentUser={userRole === 'administrateur' ? 'Administrateur' : selectedLeadForConversion.conseillere || 'Administrateur'}
        />
      )}

      {/* 🎯 Modal pour prendre rendez-vous avec un lead */}
      {showRendezVousModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Planifier un rendez-vous avec {selectedLeadForRendezVous?.prenom} {selectedLeadForRendezVous?.nom}</h3>
              <button className="close-btn" onClick={fermerRendezVousModal}>×</button>
            </div>
            <form onSubmit={ajouterRendezVous}>
              <div className="form-row">
                <div className="form-group">
                  <label>Client / Lead:</label>
                  <input
                    type="text"
                    value={nouveauRdv.client}
                    disabled
                    className="input-disabled"
                  />
                </div>
                <div className="form-group">
                  <label>Conseillère:</label>
                  <select 
                    name="conseillere" 
                    value={nouveauRdv.conseillere} 
                    onChange={handleNouveauRdvChange}
                    required={userRole !== 'conseillere'}
                    disabled={isLoadingConseillersRdv || userRole === 'conseillere'}
                  >
                    <option value="">
                      {isLoadingConseillersRdv ? 'Chargement...' : 'Sélectionner une conseillère'}
                    </option>
                    {(userRole === 'conseillere' 
                      ? [userName] 
                      : conseilleresOptions
                    ).map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Date:</label>
                  <input 
                    type="date" 
                    name="date" 
                    value={nouveauRdv.date} 
                    onChange={handleNouveauRdvChange} 
                    required 
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>
                <div className="form-group">
                  <label>Type:</label>
                  <select 
                    name="type" 
                    value={nouveauRdv.type} 
                    onChange={handleNouveauRdvChange}
                    required
                  >
                    <option value="">Sélectionner</option>
                    {typeRendezVousOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Heure de début:</label>
                  <select 
                    name="heureDebut" 
                    value={nouveauRdv.heureDebut} 
                    onChange={handleNouveauRdvChange}
                    required
                  >
                    <option value="">Sélectionner</option>
                    {creneauxHoraires.slice(0, -1).map(creneau => (
                      <option key={creneau} value={creneau}>{creneau}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Heure de fin:</label>
                  <select 
                    name="heureFin" 
                    value={nouveauRdv.heureFin} 
                    onChange={handleNouveauRdvChange}
                    required
                  >
                    <option value="">Sélectionner</option>
                    {creneauxHoraires.slice(1).map(creneau => (
                      <option 
                        key={creneau} 
                        value={creneau}
                        disabled={nouveauRdv.heureDebut && creneau <= nouveauRdv.heureDebut}
                      >
                        {creneau}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group full-width">
                  <label>Notes:</label>
                  <textarea 
                    name="notes" 
                    value={nouveauRdv.notes} 
                    onChange={handleNouveauRdvChange}
                    rows="3"
                    placeholder="Notes optionnelles..."
                  ></textarea>
                </div>
              </div>
              
              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={fermerRendezVousModal}>
                  Annuler
                </button>
                <button type="submit" className="btn-primary">
                  Créer le rendez-vous
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de confirmation de suppression */}
      {showDeleteConfirmModal && leadToDelete && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
            backdropFilter: 'blur(4px)'
          }}
          onClick={() => setShowDeleteConfirmModal(false)}
        >
          <div 
            style={{
              backgroundColor: '#1e293b',
              borderRadius: '12px',
              padding: '32px',
              maxWidth: '480px',
              width: '90%',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
              animation: 'fadeIn 0.3s ease-out'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Icône d'avertissement */}
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <div style={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                backgroundColor: '#dc2626',
                margin: '0 auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '32px'
              }}>
                🗑️
              </div>
            </div>

            {/* Titre */}
            <h2 style={{
              color: '#f1f5f9',
              fontSize: '24px',
              fontWeight: '700',
              marginBottom: '16px',
              textAlign: 'center'
            }}>
              Supprimer le lead
            </h2>

            {/* Message */}
            <p style={{
              color: '#cbd5e1',
              fontSize: '16px',
              textAlign: 'center',
              marginBottom: '32px',
              lineHeight: '1.6'
            }}>
              Êtes-vous sûr de vouloir supprimer <strong style={{ color: '#fff' }}>{leadToDelete.prenom} {leadToDelete.nom}</strong> ?
              <br />
              <span style={{ fontSize: '14px', color: '#94a3b8' }}>Cette action est irréversible.</span>
            </p>

            {/* Boutons */}
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'center'
            }}>
              <button
                onClick={cancelDelete}
                style={{
                  padding: '12px 32px',
                  backgroundColor: '#475569',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = '#64748b'}
                onMouseOut={(e) => e.target.style.backgroundColor = '#475569'}
              >
                Annuler
              </button>
              <button
                onClick={confirmDelete}
                style={{
                  padding: '12px 32px',
                  backgroundColor: '#dc2626',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = '#b91c1c'}
                onMouseOut={(e) => e.target.style.backgroundColor = '#dc2626'}
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Leads;
